import { animate, style, transition, trigger } from '@angular/animations';
import { DatePipe, } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { select, Store } from '@ngrx/store';
import {
  ColumnMode,
  SelectionType
} from '@swimlane/ngx-datatable';
import saveAs from 'file-saver';
import { LDFlagValue } from 'launchdarkly-js-client-sdk';
import * as moment from 'moment';
import { CookieService } from 'ngx-cookie';
import { MaskApplierService } from 'ngx-mask';
import { Observable, Subject } from 'rxjs';
import { debounceTime, filter, map, take, takeUntil } from 'rxjs/operators';
import {
  ActionService
} from 'src/app/core/services/action.service';
import { CommonService } from 'src/app/core/services/common.service';
import { LaunchDarklyService } from 'src/app/core/services/launchdarkly.service';
import { LoadingService } from 'src/app/core/services/loading.service';
import { RouteService } from 'src/app/core/services/route.service';
import {
  DeleteObject,
  LoadDependentRecords,
  SyncData,
} from 'src/app/core/state/_actions/form.actions';
import { LoadDetails } from 'src/app/core/state/_actions/report.actions';
import {
  ToggleEmailAlerts,
  UpdateActive,
} from 'src/app/core/state/_actions/user.actions';
import { RequestAllDataDatatable } from 'src/app/core/state/_models/company.model';
import { DependentRecords } from 'src/app/core/state/_models/form.model';
import { OpTypes } from 'src/app/core/state/_models/general.model';
import {
  Column,
  Datatable,
  RequestDatatable,
  Summary,
} from 'src/app/core/state/_models/report.models';
import { Navigation } from 'src/app/core/state/_models/table.models';
import { getAppName } from 'src/app/core/state/_selectors/application.selector';
import {
  getDependentRecords,
  getFormOperations,
} from 'src/app/core/state/_selectors/form.selector';
import { getNavigation } from 'src/app/core/state/_selectors/table.selector';
import { getUserOperations } from 'src/app/core/state/_selectors/user.selector';
import { ReportService } from 'src/app/core/state/_services/report.service';
import { FEATURE_FLAGS } from 'src/app/shared/launchdarkly.constants';
import { SubSink } from 'subsink';
import { SweetAlertOptions } from 'sweetalert2';
import { DROPDOWN_VALUE_PLACEHOLDER, EMAIL_PATTERN } from '../../constants';
import { DatatableColumn, Page } from '../../models/components.models';
import { alertPresets, AlertService, ToastTypes } from '../../services/alert.service';
import { ExportService } from '../../services/export.service';
import { SearchService } from '../../services/search.service';
import { dateOptions, intOptions, stringOptions } from '../conditions/conditions.constants';
import {
  alertDefaultExtra,
  createdbyRegex,
  CSV,
  CSV_ALL,
  datecreatedRegex,
  datemodifiedRegex,
  exportOptions,
  exportOptions2,
  exportOptions3,
  modifiedbyRegex,
  pageSizeOptions,
  PDF,
  type,
  XLSX,
} from './datatable.constants';

@Component({
  selector: 'app-ngx-datatable',
  templateUrl: './datatable.component.html',
  styleUrls: ['./datatable.component.scss'],
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.accessibility-enabled]': 'enableDataTableAccessibilityValue'
  },
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate('0.3s ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ])
  ]
})
export class DatatableComponent implements OnInit, OnChanges, OnDestroy {
  /**
   * Init pages to defaults
   */
  initPage = () => {
    const page = new Page();
    page.pageNumber = 0;
    page.size = 50;
    return page;
  };

  @ViewChild('invitedUsers', { static: false }) invitedUsers!: TemplateRef<any>;
  @ViewChild('activeUsers', { static: false }) activeUsers!: TemplateRef<any>;
  @ViewChild('emailAlerts', { static: false }) emailAlerts!: TemplateRef<any>;
  @ViewChild('checkbox', { static: false }) checkbox!: TemplateRef<any>;
  @ViewChild('default', { static: false }) default!: TemplateRef<any>;
  @ViewChild('updateActiveTemplate', { static: false }) updateActiveTemplate!: TemplateRef<any>;
  @ViewChild('fieldsreports', { static: false })
  fieldsreports!: TemplateRef<any>;
  @ViewChild('myTable', { static: false }) table!: any;
  @Input('columnMode') columnMode: ColumnMode = ColumnMode.force;
  @Input('dataset') dataset!: Datatable;
  @Input('reportId') dataTableReportId: string = '';
  @Input('tableId') tableId: string = '0';
  @Input('lookupFieldValue') lookupFieldValue: string | null = null;
  @Input('referenceFieldId') referenceFieldId: string = '0';
  @Input('referenceFieldName') referenceFieldName: any = '';
  @Input('isEmbeddedReport') isEmbeddedReport: boolean = false;
  @Input('columns') columns: Array<Column> | null = null;
  @Input('page') page: Page = this.initPage();
  @Input('actions') actions: TemplateRef<any> | null = null;
  @Input('impersonate') impersonate: TemplateRef<any> | null = null;
  @Input('notificationStatus') notificationStatus: TemplateRef<any> | null =
    null;
  @Input('passwordReset') passwordReset: TemplateRef<any> | null = null;
  @Input('pinActions') pinActions: boolean = true;
  @Input('columnOrder') columnOrder: Array<string> | null = null;
  @Input('columnOrderMode') columnOrderMode: 'lazy' | 'greedy' = 'lazy';
  @Input() trigger: Subject<boolean> = new Subject<boolean>();
  @Input('summary') summary: Summary | null = null;
  @Input('title') title: string | null = null;
  @Input('isBulkDeleteEnable') isBulkDeleteEnable: boolean = true;
  @Input('isBulkUpdateEnable') isBulkUpdateEnable: boolean = true;
  @Input('customCheckboxes') customCheckboxes: boolean = false;
  @Input('customPagination') customPagination: boolean = false;
  @Output('payload') payload = new EventEmitter<RequestDatatable>();
  @Output('tableUpdated') tableUpdated = new EventEmitter<any>();
  @Input('externalPaging') externalPaging: boolean = true;
  @Input('externalSorting') externalSorting: boolean = true;
  @Input('showEnteriesDropdown') showEnteriesDropdown: boolean = true;
  @Input('actionColumnWidth') actionColumnWidth: number = 120;
  @Input('isLightHeader') isLightHeader: boolean = false;
  @Input() datatableType: type = type.Datatable;
  @Input() showExport: boolean = true;
  @Output('onRecordDelete') onRecordDelete = new EventEmitter<boolean>();
  @Output('onRecordEdit') onRecordEdit = new EventEmitter<boolean>();
  @Input() rowHeight: number = 40;
  @Input('hideGrouping') hideGrouping: boolean = false;
  @Input('singleSelect') singleSelect: boolean = false;
  @Input('isInView') isInView: boolean = false;
  @Input('embeddedGroupColumn') embeddedGroupColumn: string = '';
  @Input('enableSummaryRow') enableSummaryRow: boolean = false;
  @Input('summaryFunction') summaryFunction: 'sum' | 'count' | 'avg' = 'count';
  @Input('fromMapInDashboard') fromMapInDashboard: boolean = false;
  @Input('isFromEmbeddedMainTabs') isFromEmbeddedMainTabs: boolean = false;
  @Input('preventFilter') preventFilter: boolean = false;
  @Input('fromDashboard') fromDashboard: boolean = false;
  @Input('fromRecordTable') fromRecordTable: boolean = false;
  @Input('auditLogId') auditLogId: string = '';
  @Input('notificationLogId') notificationLogId: string = '';
  @Input('allowInlineEdit') allowInlineEdit: boolean = false;
  @Input('allowMatchRecords') allowMatchRecords: boolean = false;
  @Input('embeddedTableName') embeddedTableName: string = '';
  @Output() embeddedMatched = new EventEmitter<any>();
  @Input('allowColumnFilter') allowColumnFilter: boolean = false;
  @Input('loadExternalUsers') loadExternalUsers: boolean = false;
  @Input('isExternalOptions') isExternalOptions: boolean = false;
  @Input('bulkUpdateTableName') bulkUpdateTableName: string = '';
  @Input() set printModeFromParent(value: boolean) {
    if (value) {
      this.isPrintMode = value;
    }
  }

  @ViewChild('searchInput', { static: false }) searchInput!: TemplateRef<any>;
  @ViewChild('selectSearchScroll', { static: false }) selectSearchScroll!: TemplateRef<any>;
  @ViewChild('enumInput', { static: false }) enumInput!: TemplateRef<any>;
  @ViewChild('encryptedField', { static: false }) encryptedField!: TemplateRef<any>;
  @ViewChild('selectAllCheckbox', { static: false }) selectAllCheckbox!: any;


  auditLogTableId: string = '0';
  notificationAuditLogTableId: string = '0';
  fromAuditLog: boolean = false;
  fromNotificationAuditLog: boolean = false;
  resizeTimeout: any;

  public renderTable: boolean = true;
  public renderColumn: boolean = true;
  public tableInstanceId: string = '';
  public DEBOUNCE_TIME = 500;
  public timeoutInstance: any = null;
  public currentPageNumber: number = this.page.pageNumber;
  public payloadSortBy: Array<any> = [];
  public payloadColumns: Array<any> = [];
  public searchPayloadColumns: Array<any> = [];
  subsink: SubSink = new SubSink();
  public payloadSearch = {
    value: '',
    regex: false,
  };
  public isGroupingSelected: boolean = false;
  private resizeSubject = new Subject<void>();
  private destroy$ = new Subject<void>();
  availableColumns: Array<DatatableColumn> = [];
  visibleColumns: Array<DatatableColumn> = [];
  filteringColumns: Array<DatatableColumn> = [];
  rows: Array<any> = [];
  exportOptions = exportOptions;
  pageSizeOptions = pageSizeOptions;
  isGrouping: boolean = false;
  isColumnSearch: boolean = false;
  isColumnFilter: boolean = false;
  loadingState: boolean = true;
  isSelectable: boolean = false;
  SelectionType = SelectionType.checkbox;
  selectedItems: Array<any> = [];
  groupedByColumn = false;
  isColumnGroupingEnable = false;
  previousTableId!: number;
  Object = Object;
  private draw = 1;
  isWhitespaces = false;
  isPrintMode = false;
  summaryPositon = 'bottom';
  createdbyRegex = createdbyRegex;
  modifiedbyRegex = modifiedbyRegex;
  datecreatedRegex = datecreatedRegex;
  datemodifiedRegex = datemodifiedRegex;
  conditionObject: any = [];
  dateOptions = dateOptions;
  stringoptions = stringOptions;
  integerOptions = intOptions;
  isColumnsGenrated: boolean = false;
  isFilterColumnApplied: boolean = false;
  buttonText: string = "";
  payloadActivateUser: any;
  previousUrl: string = '';
  isFromEditView: boolean = false;
  isSliderInteracting: boolean = false;
  conditionReportKey: any;
  filterColumn: any;
  filterRow: any;
  filterValue: any;
  filterCol: any;
  isMouseOnLink: number | null = null;
  isEditModalActive: string | null = null;
  editModalContext: any;
  editColumn!: DatatableColumn
  editRow: any;
  editValue: any;
  dirty: any = [];
  width: number = 0;
  totalColumnWidth: number = 0;
  screenReaderMessage: string = '';
  private trapFocusListener: ((e: KeyboardEvent) => void) | null = null;
  private popoverContainer: HTMLElement | null = null;
  private currentFilterButton: HTMLElement | null = null;

  // Header-to-body scroll synchronization
  private headerScrollListener: ((e: Event) => void) | null = null;
  private headerFocusListener: ((e: FocusEvent) => void) | null = null;

  /** Last 0-based page index used to avoid focusing select-all on spurious ngx-datatable (page) events */
  private lastPageOffsetForFocus!: number;

  // Feature flag for accessibility
  enableDataTableAccessibility$!: Observable<LDFlagValue>;
  enableDataTableAccessibilityValue: boolean = false;

  constructor(
    private route: RouteService,
    public search: SearchService,
    private exportService: ExportService,
    private modal: NgbModal,
    private report: ReportService,
    private alert: AlertService,
    private action: ActionService,
    private store: Store,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef,
    public commonService: CommonService,
    private maskservice: MaskApplierService,
    private cookieService: CookieService,
    public loadingService: LoadingService,
    private readonly ngZone: NgZone,
    private readonly ldService: LaunchDarklyService
  ) {
      this.loadingService.register(this.cdr);
      this.resizeSubject
        .pipe(debounceTime(100), takeUntil(this.destroy$))
        .subscribe(() => {
          this.reRenderColumn();
          this.cdr.detectChanges();
        });
  }

  // Computed properties for conditions
  get canShowBulkMatch(): boolean {
    return this.selectedItems.length > 0 && this.allowMatchRecords && this.page.size < 500;
  }

  get canShowBulkUpdate(): boolean {
    return this.selectedItems.length > 0 && this.isBulkUpdateEnable && this.page.size < 500;
  }

  get canShowBulkDelete(): boolean {
    return this.selectedItems.length > 0 && this.isBulkDeleteEnable;
  }
  // TrackBy functions
  trackByColumn(index: number, col: any): string {
    return col.prop || col.name;
  }

  trackBySize(index: number, size: number): number {
    return size;
  }

  trackByOption(index: number, option: string): string {
    return option;
  }

  trackByItem(index: number, item: any): string {
    return item.id || index;
  }

  trackByKey(index: number, key: string): string {
    return key;
  }

  /**
   * Get ARIA sort value for column header
   * Bifurcated based on feature flag
   */
  getSortAriaValue(prop: string, colName: string = ''): string | 'ascending' | 'descending' | 'none' {
    if (this.enableDataTableAccessibilityValue) {
      // FLAG ON: New accessibility-friendly messages
      if (!this.sortingColumn[prop]) return `Sort by ${colName}. Currently not sorted.`;
      return this.sortingColumn[prop] === 'asc' ? `Sort by ${colName}. Currently sorted ascending.` : `Sort by ${colName}. Currently sorted descending.`;
    } else {
      // FLAG OFF: Original simple values
      if (!this.sortingColumn[prop]) return 'none';
      return this.sortingColumn[prop] === 'asc' ? 'ascending' : 'descending';
    }
  }

  /**
   * Get human-readable sort state text
   */
  getSortStateText(prop: string): string {
    if (!this.sortingColumn[prop]) return 'not sorted';
    return this.sortingColumn[prop] === 'asc' ? 'sorted ascending' : 'sorted descending';
  }

  ngOnInit(): void {
    this.lastPageOffsetForFocus = this.currentPageNumber;
    // Initialize feature flag observable
    this.enableDataTableAccessibility$ = this.ldService.getFlagValue(
      FEATURE_FLAGS.ENABLE_DATA_TABLE_ACCESSIBILITY,
      false
    );

    // Subscribe to get synchronous value for logic
    this.subsink.add(
      this.enableDataTableAccessibility$.subscribe((value) => {
        this.enableDataTableAccessibilityValue = !!value;
      })
    );

    if (this.singleSelect) this.SelectionType = SelectionType.single;
    // Generate unique table instance ID for scoping keyboard navigation - only when flag is ON
    if (this.enableDataTableAccessibilityValue) {
      this.tableInstanceId = `datatable-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    }
    this.previousTableId = this.route.getTableKey();
    this.loadSubscribers();
    this.subsink.add(this.route.fork(() => {
      this.isColumnFilter = false;
      this.conditionObject = null;
      this.filterColumn = [];
      this.searchPayloadColumns = [];
      this.payloadColumns = [];
      this.isColumnSearch = false;
      this.summary = null;
      this.isColumnGroupingEnable = false;
      if (this.isVirtualizationEnabled) {
        this.emitPayload();
      }
    }));
    this.subsink.add(
      this.trigger?.subscribe(() => {
        let path = this.route.currentRoute();
        let deleted =
          (path.includes('reports/datatable/view') &&
            (path.includes('details') || path.includes('edit') || path.includes('add')) &&
            !path.includes('summaryFieldId')) ||
          path.includes('reports/details/view');
        this.emitPayload(false, deleted);
        this.onRecordDelete.emit(deleted);
      })
    );
    if (this.route.currentRoute().includes('/reports/datatable')) {
      this.exportOptions = exportOptions2;
    }
    if (this.route.currentRoute().includes('/fields')) {
      this.exportOptions = exportOptions3;
    }
    this.subsink.add(
      this.commonService.printForm.subscribe(() => {
        this.isPrintMode = true;
        window.addEventListener('focus', this.resetPrintMode, { once: true });
        this.table?.recalculate();
        this.cdr.detectChanges();
      })
    );

    this.isWhitespaces = true;
    this.applyClass('.datatable-body', 'custom-class2');
    if (this.isVirtualizationEnabled) {
      this.emitPayload();
    }
  }

  private resetPrintMode = (): void => {
    // 'this' here is ALWAYS the DatatableComponent instance
    //  Arrow functions capture `this` value from where they're defined, not where they're called
    this.isPrintMode = false;
    window.removeEventListener('focus', this.resetPrintMode);
  }

  applyClass(selector: string, className: string) {
    const element = document.querySelector(selector);

    if (element) {
      if (element.classList.contains(className)) {
        this.renderer.removeClass(element, className);
      } else {
        this.renderer.addClass(element, className);
      }
    }
  }

  /**
   * Detech changes of dataset
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    // Handle dataset changes
    if (changes.dataset) {
      const dataset = changes.dataset.currentValue;
      // Always hide loader and trigger change detection as soon as data arrives (empty or not)
      this.loadingState = false;
      this.ngZone.run(() => this.cdr.detectChanges()); // <-- Force change detection in Angular zone

      if (dataset?.Data?.length === 0) {
        this.rows = [];
      } else if (dataset?.Data?.length > 0) {
        this.initTable();
      }
    }

    // Handle column changes
    if (changes.columns && changes.columns.currentValue?.length > 0) {
      const columns = changes.columns.currentValue;

      this.generatePayloadColumns(columns);
      this.filteringColumns = [];
      const parsedColumns = this.makeColumnsFromPassed(columns);
      this.parseColumns(parsedColumns);

      if (this.embeddedGroupColumn && (!this.payloadSortBy.length || this.payloadSortBy[0].column === -1)) {
        this.payloadSortBy = [
          {
            column: this.getSelectedColIndex(this.embeddedGroupColumn),
            dir: 'asc',
          },
        ];
      }

      this.isColumnsGenrated = false;
      this.isFilterColumnApplied = false;
      this.conditionObject = null;
    }

    // Handle summary or embedded group column changes
    if (changes.summary || changes.embeddedGroupColumn) {
      this.setGrouping();
    }
  }

  ngAfterViewInit() {
    this.updateHeaderFormat();
    this.reRenderTable();
    this.cdr.detectChanges();

    // Setup header-to-body scroll synchronization after view is initialized
    setTimeout(() => {
      this.setupHeaderScrollSync();
    }, 100);
  }

  ngAfterViewChecked() {
    this.updateHeaderFormat();
  }

  onSliderValueChange(event: any) {
    this.filterCol.filterValue = event.minvalue;
    this.filterCol.filterValue2 = event.maxvalue
  }
  /**
   * Subscribers
   */
  loadSubscribers = () => {
    let flag = false;
    if (this.search.searchText) {
      this.payloadSearch = {
        ...this.payloadSearch,
        value: this.search.searchText,
      };
    }
    this.subsink.add(
      this.search.searchEvent.subscribe((text) => {
        if (!flag) {
          flag = true;
          return;
        }
        this.payloadSearch = {
          ...this.payloadSearch,
          value: text,
        };
        if (text && this.isColumnSearch) {
          this.toggleColumnSearch();
        }
        this.currentPageNumber = 0;
        this.lastPageOffsetForFocus = 0;
        if (!this.search.isRouteChange) {
          this.emitPayload();
        }
      })
    );
    this.subsink.add(
      this.commonService.toggleSideBar.subscribe((data: any) => {
        setTimeout(() => {
          this.resizeSubject.next();
          this.table?.recalculate();
          this.cdr.detectChanges();
        }, 500);
      })
    );
  };

  @HostListener('window:beforeunload', ['$event'])
  beforeUnloadHandler(event: Event) {
    if (this.isColumnFilter) {
      localStorage.setItem('isPageRefresh', 'true');
      localStorage.setItem('isTableHasFilter', 'true');
    }
  }

  /**
   * Set grouping on the basis of summary report
   * @returns
   */
  public setGrouping = () => {
    if (!this.summary && !this.embeddedGroupColumn) return;
    if (this.summary?.GroupBy) {
      this.rowHeight = 36;
      if (!this.isGroupingSelected) this.toggleGrouping();
      this.groupBy(
        `tbl${this.summary?.ApplicationTableKey}_${this.summary?.GroupBy}`
      );
    } else if (this.embeddedGroupColumn) {
      if (!this.isGroupingSelected) this.toggleGrouping();
      this.groupBy(`tbl${this.tableId}_${this.embeddedGroupColumn}`);
    } else {
      if (this.isGroupingSelected) this.toggleGrouping();
      else {
        this.isGrouping = false;
      }
      this.reRenderTable();
    }
  };

  /**
   * Init datatable
   */
  public initTable = () => {
    if (!this.dataset || !Object.keys(this.dataset?.Data).length) return;

    // Parse columns from passed columns or dataset
    const parsedColumns = this.columns
      ? this.makeColumnsFromPassed(this.columns)
      : this.makeColumnsFromDataset(this.dataset?.Data);
    this.parseColumns(parsedColumns);

    // Handle grouping logic
    if (this.groupedByColumn && this.groupByName) {
      this.groupArrayBy(this.dataset?.Data, this.groupByName);
    } else if (this.summary?.GroupBy || this.embeddedGroupColumn) {
      this.handleDefaultGrouping();
    } else {
      this.handleDefaultRows();
    }

    // Clear selection if items selected
    if (this.selectedItems.length) {
      this.clearSelection();
    }

    // Check if the table is being loaded from an edit or details view
    this.isFromEditView = this.checkIfFromEditView();

    // Apply filters from cookies if applicable
    this.applyFiltersFromCookies();

    // Update total elements for pagination
    this.page.totalElements = this.dataset?.RecordsTotal;
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 500)
  };

  private handleDefaultGrouping = () => {
    let groupby = '';
    const regexp = new RegExp(`^tbl${this.tableId}_.*${this.groupByName}$`);

    if (!this.groupByName.includes('tbl')) {
      if (this.dataset.Data) {
        for (const item in this.dataset.Data[0]) {
          if (regexp.test(item)) {
            groupby = item;
            break;
          }
        }
      }
    } else {
      groupby = this.groupByName;
    }

    this.groupArrayBy(this.dataset?.Data, groupby);
  };

  private handleDefaultRows = () => {
    if (document.documentElement.lang !== 'en') {
      this.loadingState = true;
    }

    this.rows = this.dataset?.Data?.map((data) => ({
      ...data,
      showActions: false,
    })) || [];
    // this.cdr.detectChanges();

    if (document.documentElement.lang !== 'en') {
      this.loadingState = false;
    }
  };

  private checkIfFromEditView = (): boolean => {
    const previousUrl = this.route.previousUrl;
    const isEditOrAddView =
      previousUrl.includes('form') &&
      (previousUrl.includes('edit') || previousUrl.includes('add'));
    const isDetailsView = previousUrl.includes('details');

    return (
      previousUrl.includes(this.reportId || this.dataTableReportId) &&
      (isDetailsView || isEditOrAddView)
    );
  };

  private applyFiltersFromCookies = () => {
    if (
      (this.reportId || this.dataTableReportId) &&
      !this.isFilterColumnApplied &&
      this.isColumnsGenrated &&
      (this.isFromEditView || this.preventFilter || localStorage.getItem('isPageRefresh')) &&
      localStorage.getItem('isTableHasFilter')
    ) {
      const cookieKey = `datatable_${this.fromDashboard}_${this.isEmbeddedReport}_${this.reportId || this.dataTableReportId}`;
      const dataFromCookie = this.cookieService.get(cookieKey);

      if (dataFromCookie) {
        const parsedData = JSON.parse(dataFromCookie);
        const filterColumns = parsedData.filteringColumns;

        this.filteringColumns = this.filteringColumns.map((column) => {
          const matchedColumn = filterColumns.find(
            (col: any) =>
              col[this.isColumnFilterAllowed ? 'fieldName' : 'fieldKey'] ===
              column[this.isColumnFilterAllowed ? 'fieldName' : 'fieldKey']
          );

          if (matchedColumn) {
            column.filterValue = matchedColumn.filterValue;
            column.filterValue2 = matchedColumn.filterValue2;
            column.filterCondition = matchedColumn.filterCondition;
            column.isFilterApplied = matchedColumn.isFilterApplied;
          }

          return column;
        });

        this.conditionObject = parsedData.conditionObject;
        this.conditionReportKey = this.reportId || this.dataTableReportId;

        this.emitPayload();
        this.isColumnFilter = true;
        this.isFilterColumnApplied = true;

        localStorage.removeItem('isPageRefresh');
        localStorage.removeItem('isTableHasFilter');
      }
    }
  };

  /**
   * @returns report id to update
   */
  get reportId() {
    return this.route.paramMap.reportId;
  }

  /**
   * @returns current record id to update
   */
  get recordId() {
    return this.route.paramMap.id;
  }

  /**
   * Datatable page event
   * @param event
   */
  pageListener = (event: any) => {
    this.clearSelection();
    const newOffset =
      (this.reportId || this.fromMapInDashboard) && this.isVirtualizationEnabled
        ? event.page - 1
        : event.offset;
    const shouldFocusSelectAll = this.lastPageOffsetForFocus !== newOffset;
    this.currentPageNumber = newOffset;
    this.lastPageOffsetForFocus = newOffset;
    this.emitPayload();

    // WCAG 2.4.3 Focus Order (Level A) - Move focus to select all checkbox only on real page change
    if (shouldFocusSelectAll) {
      this.focusSelectAllCheckbox();
    }
  };

  /**
   * Handle page change from custom pagination component
   * WCAG 4.1.3 Status Messages (Level AA) - Announce page changes to screen readers
   * @param page 1-indexed page number
   * Only active when accessibility flag is ON
   */
  onPageChange = (page: number) => {
    if (!this.enableDataTableAccessibilityValue) {
      // Should not be called when flag is OFF
      return;
    }

    this.clearSelection();
    this.currentPageNumber = page - 1; // Convert to 0-indexed
    this.lastPageOffsetForFocus = this.currentPageNumber;

    // Calculate values for screen reader announcement
    const totalPages = Math.ceil(this.page.totalElements / this.page.size);
    const startRecord = this.currentPageNumber * this.page.size + 1;
    const endRecord = Math.min(
      (this.currentPageNumber + 1) * this.page.size,
      this.page.totalElements
    );

    // WCAG 4.1.3 Status Messages (Level AA)
    // Announce page change with context to screen readers
    this.screenReaderMessage = `Page ${this.currentPageNumber + 1} of ${totalPages}, showing ${startRecord} to ${endRecord} of ${this.page.totalElements} records`;

    this.emitPayload();

    // WCAG 2.4.3 Focus Order (Level A) - Move focus to select all checkbox
    this.focusSelectAllCheckbox();
  };

  /**
   * Focus the "Select all rows" checkbox after page change
   * WCAG 2.4.3 Focus Order (Level A) - Maintain logical focus order
   * Only focuses when custom checkboxes are enabled and visible
   * Only active when accessibility flag is ON
   */
  private focusSelectAllCheckbox(): void {
    if (!this.enableDataTableAccessibilityValue) {
      // Don't focus when flag is OFF
      return;
    }

    // Only focus if custom checkboxes are enabled and conditions allow checkbox to be visible
    if (!this.customCheckboxes ||
        !(this.isBulkDeleteEnable || this.isBulkUpdateEnable) ||
        this.isGrouping ||
        this.isPrintMode) {
      return;
    }

    // Use setTimeout to ensure DOM has updated after pagination change
    // ngx-datatable may need time to re-render the table content
    setTimeout(() => {
      try {
        if (this.selectAllCheckbox?.nativeElement) {
          this.selectAllCheckbox.nativeElement.focus();
        }
      } catch {
        // Silently handle any focus errors to avoid breaking the application
      }
    }, 100);
  }

  sortingColumn: any = {};

  /**
   * Sort listener
   */
  sortListener = (event: any) => {
    this.payloadSortBy = [
      {
        column: this.getSelectedColIndex(event.sorts[0].prop),
        dir: event.sorts[0].dir,
      },
    ];
    this.sortingColumn = {
      [event.sorts[0].prop]: event.sorts[0].dir
    };
    this.emitPayload();
  };

  /**
   * On filter submit
   */
  onFilter = () => {
    this.emitPayload();
  };

  /**
   * @returns index of column selected for sorting
   */
  getSelectedColIndex = (colName: string) => {
    let index =
      this.payloadColumns.findIndex((col) => col.data === colName) || 0;
    return index;
  };

  /**
   * Get payload
   * @param pageNumber current page number
   * @param pageSize length of data per page
   * @returns payload
   */
  getPayload = (deleted: boolean): RequestDatatable => {
    return <RequestDatatable>{
      ReportKey: this.reportId,
      TableKey: this.route.getTableKey(),
      Draw: this.draw++,
      Start:
        this.currentPageNumber * this.page.size > this.page.totalElements
          ? 0
          : this.currentPageNumber * this.page.size,
      Length: this.page.size,
      Order: !this.payloadSortBy.length
        ? this.summary?.GroupBy || this.embeddedGroupColumn
          ? [
            {
              column: this.getSelectedColIndex(
                this.summary?.GroupBy || this.embeddedGroupColumn
              ),
              dir: 'asc',
            },
          ]
          : [
            {
              column: -1,
              dir: 'asc',
            },
          ]
        : this.payloadSortBy[0].column === -1
          ? this.summary?.GroupBy || this.embeddedGroupColumn
            ? [
              {
                column: this.getSelectedColIndex(
                  this.summary?.GroupBy || this.embeddedGroupColumn
                ),
                dir: 'asc',
              },
            ]
            : this.payloadSortBy
          : this.payloadSortBy,
      Columns:
        this.searchPayloadColumns.length > 0
          ? this.searchPayloadColumns
          : this.payloadColumns,
      Search: this.payloadSearch,
      IsGroupingEnabled: this.groupedByColumn
        ? true
        : this.summary?.GroupBy || this.embeddedGroupColumn
          ? true
          : false,
      IsQueryReport: false,
      FromEmbeddedReport: deleted,
      Template: this.selectedItems,
      ConditionGroups: Array.isArray(this.conditionObject) && this.conditionObject.length && (this.conditionReportKey === this.reportId || this.conditionReportKey === this.dataTableReportId || this.allowColumnFilter) ? this.conditionObject : null
    };
  };

  columnsWithTableId = (columns: any[]) => {
    columns.forEach((c) => {
      if (!c.ApplicationTableId) c.ApplicationTableId = this.tableId;
    });

    return columns;
  };

  /**
   * Make column array for payload or passed columns
   * Generates column from first row of dataset or from passed columns
   */
  generatePayloadColumns = (tempColumns: Column[] = []) => {
    if (tempColumns.length > 0) {
      this.payloadColumns = [];
      for (let col of tempColumns)
        this.payloadColumns.push({
          data: col.FieldName,
          name: '',
          searchable: true,
          orderable: true,
          search: {
            value: '',
            regex: false,
          },
        });
      this.payloadColumns.push({
        data: '',
        name: '',
        searchable: true,
        orderable: true,
        search: {
          value: '',
          regex: false,
        },
      });
      this.columns = tempColumns;
    } else if (this.columns?.length) {
      // if columns passed
      this.payloadColumns = [];
      for (let col of this.columns)
        this.payloadColumns.push({
          data: col.FieldName,
          name: '',
          searchable: true,
          orderable: true,
          search: {
            value: '',
            regex: false,
          },
        });
      this.payloadColumns.push({
        data: '',
        name: '',
        searchable: true,
        orderable: true,
        search: {
          value: '',
          regex: false,
        },
      });
    } else if (this.dataset?.Data?.length) {
      this.payloadColumns = [];
      const row = { ...this.dataset?.Data[0] };
      for (let col in row) {
        if (!col.includes('_url') && !(col === 'id' && (this.auditLogId || this.notificationLogId))) {
          this.payloadColumns.push({
            data: col,
            name: '',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false,
            },
          });
        }
      }
      if (this.auditLogId || this.notificationLogId) {
        this.payloadColumns.push({
          data: '',
          name: '',
          searchable: true,
          orderable: true,
          search: {
            value: '',
            regex: false,
          },
        });
      }
    }
  };

  /**
   * Make columns from dataset if not explicitly passed
   */
  private makeColumnsFromDataset = (rows: Array<any>) => {
    const columns: Array<DatatableColumn> = [];
    // var fieldNames = [];
    // for (let row in rows[0]) {
    //   fieldNames.push(row);
    // }

    for (let row in rows[0]) {
      if (
        row.includes('_parent_id') ||
        row.includes('_parent_table_id') ||
        row.includes('_parent_table_key')
      )
        continue;

      let fieldName = row;
      while (fieldName.includes('tbl')) {
        fieldName = fieldName.split('_').splice(1).join('_');
      }

      //hide isUnique from Fields Listing
      if (
        fieldName.toLowerCase() === 'isunique' &&
        this.route.currentRoute().includes('fields')
      )
        continue;

      columns.push({
        fieldName: row,
        name: fieldName.replace(/_/g, ' '),
        prop: row,
      });

      // Note: might be needed
      // this.columns?.push({
      //   Key: '',
      //   Name: fieldName.replace(/_/g, ' '),
      //   FieldName: row,
      //   FieldType: '',
      //   LookupTargetFieldKey:
      //     fieldNames.includes(`${row}_parent_id`)
      //       ? 'Temp'
      //       : '',
      //   ApplicationTableKey: '',
      //   Formula: '',
      //   HasLinkText: false,
      //   IsCopy: false,
      //   IsUnique: false,
      //   IsFormula: false,
      //   IsHtmlAllowed: false,
      //   IsSummaryField: false,
      //   IsUrl: false,
      //   ReportKey: '',
      //   IsExternalUrl: false,
      //   ShouldBuildViews: false,
      //   IsEvaluated: false,
      //   LastRun: 0,
      // });
    }
    return columns;
  };

  totalSum = (cells: any[]) => {
    const filteredCells = cells.filter(cell => !!(+cell));
    const total = filteredCells.reduce((sum, cell) => (sum += +cell), 0);
    if (this.columns && this.columns?.[1]?.FieldType === 'DECIMAL(20,2)') {
      let val = total?.toString()?.split('.');
      const isNegative = val?.[0]?.startsWith('-');
      const absoluteValue = isNegative ? val?.[0]?.slice(1) : val?.[0];
      const formattedValue = '$' + this.separator(absoluteValue) + (val?.[1] ? '.' + val[1] : '');
      return isNegative ? '-' + formattedValue : formattedValue;
    }
    return total;
  }

  columnsAvg(cells: any[]) {
    const filteredCells = cells.filter(cell => !!(+cell));
    return filteredCells.reduce((sum, cell) => (sum += +cell), 0) / filteredCells.length;
  }

  totalCount(cells: any[]) {
    return cells.filter(cell => cell !== null && cell !== undefined).length;
  }

  nullSummary = () => 'Total';

  /**
   * Make columns from explicitly passed columns
   */
  public makeColumnsFromPassed = (passed: Array<any>) => {
    const columns: Array<DatatableColumn> = [];
    for (let column of passed) {
      columns.push({
        fieldName: column.FieldName,
        name: column.ReportColumnSettings?.AlternateColumnName ? column.ReportColumnSettings.AlternateColumnName : column.Name,
        prop: column.FieldName,
        fieldType: column.FieldType,
        fieldKey: column.Key,
        isRelation: column.IsRelation,
        isLookup: column.IsLookupField,
        minValue: column.MinValue,
        maxValue: column.MaxValue,
        allowDecrypt: column.IsDecryptionAllowed,
        dropdownValues: this.isExternalOptions ? column.Values : column.Values?.map((option: any) => ({ label: option.Value, value: option.Value })),
        lookupTargetFieldId: column.LookupTargetFieldId,
        IsSummaryField: column.IsSummaryField,
        proxyFieldKey: column.ProxyFieldKey,
        proxyFieldType: column.ProxyFieldType,
        proxyDecryptionAllowed: column.ProxyDecryptionAllowed,
        IsNullable: column.IsNullable,
        showSummary: column.ShowSummary
      });
    }
    return columns;
  };

  onSliderMouseDown(): void {
    this.isSliderInteracting = true;
  }

  onSliderMouseUp(): void {
    setTimeout(() => {
      this.isSliderInteracting = false;
    }, 100)
  }

  onSliderMouseLeave(): void {
    this.isSliderInteracting = false;
  }

  /**
   * Create column data for datatable
   */
  private parseColumns = (columnData: Array<DatatableColumn>) => {
    if (!columnData.length) return;
    let columns: Array<DatatableColumn> = [];
    for (let o of columnData)
      if (o.fieldName !== 'id') {
        if (o.dropdownValues && !this.isExternalOptions && (o.fieldType === 'ENUM' || o.proxyFieldType === 'ENUM')) {
          o.dropdownValues.unshift({
            label: DROPDOWN_VALUE_PLACEHOLDER,
            value: ''
          });
        }
        columns.push({
          name: o.name,
          prop: o.prop,
          fieldName: o.fieldName,
          fieldType: o.fieldType,
          fieldKey: o.fieldKey,
          isRelation: o.isRelation,
          isLookup: o.isLookup,
          filterCondition: null,
          filterValue: '',
          filterValue2: '',
          isFilterApplied: false,
          minValue: o.minValue,
          maxValue: o.maxValue,
          dropdownValues: o.dropdownValues,
          allowDecrypt: o.allowDecrypt,
          lookupTargetFieldId: o.lookupTargetFieldId,
          proxyFieldType: o.proxyFieldType,
          proxyFieldKey: o.proxyFieldKey,
          proxyDecryptionAllowed: o.proxyDecryptionAllowed,
          IsSummaryField: o.IsSummaryField,
          isHyperLink: this.isHyperlink(o),
          isUrl: this.isUrl(o),
          isFile: this.isFile(o),
          isHtmlallowed: this.isHtmlAllowed(o),
          isEmail: this.isEmailField(o),
          valueAlign: this.valueAlign(o),
          isEditAllowed: this.isEditAllowed(o),
          IsNullable: o.IsNullable,
          showSummary: o.showSummary
        });
      }
    // this.visibleColumns = [];
    this.availableColumns = [];
    this.availableColumns = this.orderByColumnOrder(columns);
    this.visibleColumns =
      JSON.stringify(this.visibleColumns) ===
        JSON.stringify(this.availableColumns)
        ? this.visibleColumns
        : [...this.availableColumns];
    if (!this.isColumnsGenrated && !this.isFilterColumnApplied) {
      this.filteringColumns = JSON.parse(JSON.stringify(this.visibleColumns));
      this.isColumnsGenrated = true;
    }
    this.cdr.detectChanges();
  };

  /**
   * Sort columns by columnOrder
   * @param columns
   */
  orderByColumnOrder = (columns: Array<DatatableColumn>) => {
    if (!this.columnOrder || !this.columnOrder.length) return columns;

    const columnMap: any = columns.reduce(
      (accumulator, currentValue) => ({
        ...accumulator,
        [currentValue.fieldName]: currentValue,
      }),
      {}
    );

    const result = this.columnOrder
      .filter((key) => columnMap[key])
      .map((key) => {
        const o = { ...columnMap[key] };
        delete columnMap[key];
        return o;
      });

    if (this.columnOrderMode === 'greedy')
      return result.concat(Object.keys(columnMap).map((key) => columnMap[key]));

    return result;
  };

  /**
   * Filter out parent and parent table id properties
   * from columns
   */
  filterParent = (column: any) =>
    !column.prop.includes('_parent_id') &&
    !column.prop.includes('_parent_table_id') &&
    !column.prop.includes('_parent_table_key');

  /**
   * Toggle isGrouping
   * @param column's prop
   */
  toggleGrouping = () => {
    this.groupedByColumn = false;
    this.isColumnGroupingEnable = false;
    this.isGroupingSelected = !this.isGroupingSelected;
    if (!this.isGroupingSelected) {
      this.payloadSortBy = [
        {
          column: 0,
          dir: 'asc',
        },
      ];
      this.isGrouping = false;
    }
  };

  /**
   * Group by
   * @param column's prop
   */
  groupBy = (prop: string) => {
    if (this.isGroupingSelected) {
      this.isGrouping = true;
      this.payloadSortBy = [
        {
          column: this.getSelectedColIndex(prop),
          dir: 'asc',
        },
      ];
      this.reRenderTable();
    }
  };

  toggleGroupingColumn(prop: string) {
    this.groupedByColumn = true;
    this.isGrouping = true;
    this.payloadSortBy = [
      {
        column: this.getSelectedColIndex(prop),
        dir: 'asc',
      },
    ];
    this.reRenderTable();
  }

  groupArrayBy(originalArray: Array<object>, groupBy: string) {
    let priviousKeyValue: any;
    let previousKeyParentId: any;
    let previousKeyParentTableKey: any;
    let tempArray: object[] = [];
    originalArray.forEach((item: any) => {
      item = { ...item };
      if (item[groupBy] !== '') {
        // item[groupBy] =
        //   item[groupBy].charAt(0).toLocaleUpperCase() +
        //   item[groupBy].slice(1, item[groupBy].length).toLocaleLowerCase();
        if (typeof groupBy === 'string' && groupBy?.includes('related')) {
          previousKeyParentId = item[`${groupBy}_parent_id`];
          previousKeyParentTableKey = item[`${groupBy}_parent_table_key`];
        }
        priviousKeyValue = item[groupBy];
        tempArray.push(item);
      } else {
        item[groupBy] = priviousKeyValue;
        if (groupBy.includes('related')) {
          item[`${groupBy}_parent_id`] = previousKeyParentId;
          item[`${groupBy}_parent_table_key`] = previousKeyParentTableKey;
        }
        tempArray.push(item);
      }
      if (originalArray.length == tempArray.length) {
        this.rows = tempArray;
      }
    });
  }

  getTotalRecords(data: any) {
    const d = data.filter(
      (e: any) => e.isgrouped === 'true' && e.tot_count != ''
    );
    if (d.length) {
      return d[0]?.tot_count ? d[0]?.tot_count : data?.length - 1;
    }
    return data.length;
  }

  /**
   * Toggle column search
   */
  toggleColumnSearch = () => {
    if (this.search.disabled) return;
    if (this.isColumnFilter) {
      this.isColumnFilter = false;
      this.removeFilterOrSearch();
    }
    this.isColumnSearch = !this.isColumnSearch;
    if (!this.isColumnSearch) {
      this.removeFilterOrSearch();
    }
    this.initTable();
  };

  /**
   * Toggle Column Filter
   */
  toggleColumnFilter = () => {
    if (this.search.filterdisabled) return;
    if (this.isColumnSearch) {
      this.isColumnSearch = false;
      this.removeFilterOrSearch();
    }
    this.isColumnFilter = !this.isColumnFilter;
    if (!this.isColumnFilter) {
      this.removeFilterOrSearch();
      this.cookieService.remove(`datatable_${this.fromDashboard}_${this.isEmbeddedReport}_${this.reportId || this.dataTableReportId}`);
    }
    this.initTable();
  };

  removeFilterOrSearch() {
    this.conditionObject = null;
    this.conditionReportKey = null;
    this.filterCol = null;
    const payloadColumns = JSON.parse(JSON.stringify(this.payloadColumns));
    for (let col of payloadColumns) col.search.value = '';
    this.searchPayloadColumns = payloadColumns;
    this.filteringColumns.forEach((column: DatatableColumn) => {
      column.isFilterApplied = false;
      column.filterCondition = null;
      column.filterValue = '';
      column.filterValue2 = '';
    })
    this.emitPayload();
  }

  onColumnGroupingEnebled() {
    this.isColumnGroupingEnable = !this.isColumnGroupingEnable;
    if (!this.isColumnGroupingEnable) {
      this.reRenderTable();
      this.groupedByColumn = false;
      if (this.isVirtualizationEnabled && this.isGrouping) {
        this.payloadSortBy = [
          {
            column: -1,
            dir: 'asc',
          },
        ];
        this.emitPayload();
      }
      if (!(this.summary?.GroupBy || this.embeddedGroupColumn)) {
        this.isGrouping = false;
      }
      // this.rows.forEach((item, index) => {
      //   if(item.id === "") this.rows.splice(index,1);
      //   item.isgrouped = 'false';
      // });
      // this.reRenderTable();
    } else {
      this.isGroupingSelected = false;
      this.payloadSortBy = [
        {
          column: -1,
          dir: 'asc',
        },
      ];
    }
  }

  /**
   * Emit payload
   */
  emitPayload = (useDebounce: boolean = false, deleted: boolean = false) => {
    if (this.isGrouping) this.rows = [];
    if (this.timeoutInstance) clearTimeout(this.timeoutInstance);
    this.timeoutInstance = null;

    let payload = this.getPayload(deleted);
    if (useDebounce)
      this.timeoutInstance = setTimeout(() => {
        this.loadingState = true;
        this.payload.emit(payload);
      }, this.DEBOUNCE_TIME);
    else {
      this.loadingState = true;
      this.payload.emit(payload);
    }
  };

  /**
   * Button actions
   * @param option one of available export options
   */
  onExport = (option: string) => {
    const route = this.route.currentRoute();
    var appName = 'UsersForApplication_';
    var currentReportId = this.reportId;
    var currentTableId = this.route.getTableKey();

    if (
      route.includes('settings/user-management/users') ||
      route.includes('settings/user-management/tracking') ||
      route.includes('settings/audit-log')
    ) {
      this.subsink.add(
        this.store
          .pipe(
            select(getAppName),
            filter((apps) => !!apps),
            map((apps) => apps)
          )
          .subscribe((apps: any) => {
            if (apps[this.route.getApplicationKey()] != undefined) {
              appName = apps[this.route.getApplicationKey()] + '_';
            }
          })
      );
    }
    if (route.includes('reports/datatable/view') && route.includes('details')) {
      currentReportId = this.dataTableReportId;
      currentTableId = this.tableId;
    }

    if (
      (currentReportId === undefined || isNaN(currentReportId)) &&
      this.dataTableReportId != undefined
    )
      currentReportId = this.dataTableReportId;

    if (
      (currentTableId === undefined || isNaN(currentTableId)) &&
      this.tableId != undefined
    )
      currentTableId = this.tableId;

    const fileName = route.includes('clients')
      ? `Clients`
      : route.includes('company')
        ? `Companies`
        : route.includes('settings/user-management/users')
          ? appName + 'users'
          : route.includes('settings/user-management/tracking')
            ? appName + 'UserTracking'
            : route.includes('app-log')
              ? appName + 'AuditLog'
              : route.includes('notification-log')
                ? appName + 'NotificationLog'
                : `Table_${currentTableId}_Report_${currentReportId}`;

    let standardCols: any = [];
    this.visibleColumns.forEach(column => {
      if (column.fieldType === 'PII2' || column.fieldType === 'PII') {
        standardCols.push(column.fieldName);
      }
    });
    switch (option) {
      case XLSX:
        if (route.includes('settings/audit-log') || ((this.auditLogId || this.notificationLogId) && this.summary?.DefaultReport)) {
          if (route.includes('notification-log') || (this.notificationLogId)) {
            this.exportLogReports(this.notificationLogId, 'excel', fileName);
          } else if (route.includes('app-log') || (this.auditLogId)) {
            this.exportLogReports(this.auditLogId, 'excel', fileName);
          }
        } else {
          if (standardCols.length) {
            this.exportWithAcknoklegdement(option, fileName);
          } else {
            this.exportReportData(option, fileName);
          }
        }
        break;
      case CSV:
        if (route.includes('settings/audit-log') || ((this.auditLogId || this.notificationLogId) && this.summary?.DefaultReport)) {
          if (route.includes('notification-log') || (this.notificationLogId)) {
            this.exportLogReports(this.notificationLogId, 'csv', fileName);
          } else if (route.includes('app-log') || (this.auditLogId)) {
            this.exportLogReports(this.auditLogId, 'csv', fileName);
          }
        } else {
          if (standardCols.length) {
            this.exportWithAcknoklegdement(option, fileName);
          } else {
            this.exportReportData(option, fileName);
          }
        }
        break;
      case CSV_ALL:
        if (
          route == '/company' ||
          route == '/clients' ||
          route.includes('settings/user-management/users') ||
          route.includes('settings/user-management/tracking')
        ) {
          this.exportAllData(route, option, fileName);
        } else if (route.includes('settings/audit-log/notification-log')) {
          this.subsink.add(
            this.store
              .pipe(
                select(getNavigation),
                filter((tables) => !!tables[this.route.getApplicationKey()]),
                map((tables) => tables[this.route.getApplicationKey()])
              )
              .subscribe((tables) => {
                const notificationAuditLogTable = tables.filter(
                  (item: Navigation) => item.Title == 'Notification Audit Log'
                );

                if (notificationAuditLogTable.length > 0) {
                  this.notificationAuditLogTableId = notificationAuditLogTable[0].LevelIds.TableId;
                  this.fromNotificationAuditLog = true;
                }
              })
          );
          this.exportReportData(option, fileName);
        } else if (route.includes('settings/audit-log/app-log')) {
          this.subsink.add(
            this.store
              .pipe(
                select(getNavigation),
                filter((tables) => !!tables[this.route.getApplicationKey()]),
                map((tables) => tables[this.route.getApplicationKey()])
              )
              .subscribe((tables) => {
                const auditLogTable = tables.filter(
                  (item: Navigation) => item.Title == 'Audit Log'
                );

                if (auditLogTable.length > 0) {
                  this.auditLogTableId = auditLogTable[0].LevelIds.TableId;
                  this.fromAuditLog = true;
                }
              })
          );
          this.exportReportData(option, fileName);
        } else {
          if (standardCols.length) {
            this.exportWithAcknoklegdement(option, fileName);
          } else {
            this.exportReportData(option, fileName);
          }
        }
        break;
      // case PDF:
      //   this.exportService.pdf(fileName, this.clearColumns(this.rows));
      //   break;
      case PDF:
        if (route.includes('settings/audit-log/notification-log')) {
          this.subsink.add(
            this.store
              .pipe(
                select(getNavigation),
                filter((tables) => !!tables[this.route.getApplicationKey()]),
                map((tables) => tables[this.route.getApplicationKey()])
              )
              .subscribe((tables) => {
                const notificationAuditLogTable = tables.filter(
                  (item: Navigation) => item.Title == 'Notification Audit Log'
                );

                if (notificationAuditLogTable.length > 0) {
                  this.notificationAuditLogTableId = notificationAuditLogTable[0].LevelIds.TableId;
                  this.fromNotificationAuditLog = true;
                }
              })
          );
          this.exportReportData(option, fileName);
        } else if (route.includes('settings/audit-log/app-log')) {
          this.subsink.add(
            this.store
              .pipe(
                select(getNavigation),
                filter((tables) => !!tables[this.route.getApplicationKey()]),
                map((tables) => tables[this.route.getApplicationKey()])
              )
              .subscribe((tables) => {
                const auditLogTable = tables.filter(
                  (item: Navigation) => item.Title == 'Audit Log'
                );

                if (auditLogTable.length > 0) {
                  this.auditLogTableId = auditLogTable[0].LevelIds.TableId;
                  this.fromAuditLog = true;
                }
              })
          );
          this.exportReportData(option, fileName);
        } else if (
          route == '/company' ||
          route == '/clients' ||
          route.includes('settings/user-management/users') ||
          route.includes('settings/user-management/tracking')
        ) {
          this.exportAllData(route, option, fileName);
        } else {
          if (standardCols.length) {
            this.exportWithAcknoklegdement(option, fileName);
          } else {
            this.exportReportData(option, fileName);
          }
          break;
        }
    }
  };

  exportLogReports(tableId: string, exportType: string, fileName: string) {
    this.subsink.add(
      this.report
        .exportReport(
          tableId,
          0,
          exportType,
          this.page.size,
          this.currentPageNumber * this.page.size > this.page.totalElements
            ? 0
            : this.currentPageNumber * this.page.size,
          this.lookupFieldValue,
          this.referenceFieldId,
          this.isEmbeddedReport,
        )
        .pipe(take(1))
        .subscribe((data: any) => {
          // using fileName for pdf which comes from API instead of local one
          if (data && data.File != undefined)
            saveAs(data.File, fileName);
        })
    );
  }

  exportWithAcknoklegdement(option: any, fileName: any) {
    this.alert
      .fire(<SweetAlertOptions>{
        ...alertPresets.delete,
        title: 'FOR OFFICIAL USE ONLY',
        text: 'The following action may disclose sensitive information. Please proceed with caution. If this action was not intended, please select cancel.',
        confirmButtonText: 'I agree',
        customClass: {
          ...alertPresets.delete.customClass,
          actions: 'w-auto d-flex justify-content-center'
        }
      })
      .then(async (res) => {
        if (res.isConfirmed) {
          this.exportReportData(option, fileName);
        }
      }).catch(err => { })
  }

  exportReportData(option: any, fileName: any) {
    switch (option) {
      case XLSX:
        this.exportService.xlsx(
          fileName,
          'Sheet 1',
          this.clearColumns(this.rows)
        );
        break;
      case CSV:
        this.exportService.csv(fileName, this.clearColumns(this.rows));
        break;
      case CSV_ALL:
        this.exportFromBlob('csv', fileName);
        break;
      case PDF:
        this.exportFromBlob('pdf', fileName);
        break;
    }
  }

  exportAllData(route: string, option: string, fileName: string) {
    const exportType = option === CSV_ALL ? 'csv' : 'pdf';

    const requestPayload: RequestAllDataDatatable = {
      Draw: 1,
      IsGroupingEnabled: false,
      Length: 10,
      Order: [],
      Search: { regex: false, value: '' },
      Start: 0,
      Columns: [],
      GetAll: true,
      ExportType: exportType
    };

    const routeMap: any = {
      '/clients': { url: this.report.GET_CLIENTS_EXPORT_URL, defaultFileName: 'All Clients' },
      '/company': { url: this.report.GET_COMPANY_EXPORT_URL, defaultFileName: 'All Companies' },
      'settings/user-management/users': { url: this.report.GET_USERS_EXPORT_URL, defaultFileName: 'All Users', needsApplicationKey: true },
      'settings/user-management/tracking': { url: this.report.GET_IPTRACKING_EXPORT_URL, needsApplicationKey: true }
    };

    const exportData = (url: string, defaultFileName: string) => {
      this.subsink.add(
        this.report.getAllData(url, requestPayload)
          .pipe(take(1))
          .subscribe((data: any) => {
            if (data && data.File) {
              const exportFileName = exportType === 'pdf' ? data.FileName : defaultFileName;
              saveAs(data.File, exportFileName);
            }
          })
      );
    };

    for (const key in routeMap) {
      if (route === key || route.includes(key)) {
        const { url, defaultFileName, needsApplicationKey } = routeMap[key];
        if (needsApplicationKey) {
          requestPayload.ApplicationKey = this.route.getApplicationKey();
        }
        exportData(url, defaultFileName || fileName);
        break;
      }
    }
  }

  /**
   * Export from blob
   * @param data
   */
  exportFromBlob(exportType: string, fileName: string) {
    const route = this.route.currentRoute();
    var currentReportId = this.reportId;
    if (this.dataTableReportId) {
      currentReportId = this.dataTableReportId;
    }
    var tableId = this.tableId;
    if (this.fromAuditLog) {
      tableId = this.auditLogTableId;
    } else if (this.fromNotificationAuditLog) {
      tableId = this.notificationAuditLogTableId;
    }
    this.subsink.add(
      this.report
        .exportReport(
          tableId,
          currentReportId ?? 0,
          exportType,
          -1,
          0,
          this.lookupFieldValue,
          this.referenceFieldId,
          this.isEmbeddedReport,
        )
        .pipe(take(1))
        .subscribe((data: any) => {
          // using fileName for pdf which comes from API instead of local one
          if (data && data.File != undefined)
            saveAs(data.File, exportType === 'pdf' ? data.FileName : fileName);
        })
    );
    this.fromAuditLog = false;
    this.fromNotificationAuditLog = false;
  }

  /**
   * Size of page selected
   */
  onPageSizeSelect = (size: number) => {
    this.page.size = size;
    this.screenReaderMessage = `Showing ${size} rows per page`;
    this.emitPayload();
  };

  openFilters = async () => {
    const { FiltersComponent } = await import('./filters/filters.component');
    const modal = this.modal.open(FiltersComponent, {
      windowClass: 'modal-custom',
      centered: true,
      size: 'lg',
      backdrop: 'static',
      animation: true,
      keyboard: false,
    });

    modal.componentInstance.availableColumns = this.availableColumns;
    modal.componentInstance.visibleColumns = this.visibleColumns;

    this.subsink.add(
      modal.componentInstance.selectedChange.subscribe(
        (columns: Array<DatatableColumn>) => {
          this.visibleColumns = columns;
        }
      )
    );
  };

  /**
   * Column search input listener
   * @param event input event
   * @param column column properties
   */
  onColumnSearch = (event: any, column: DatatableColumn, col: DatatableColumn, isFromSelectSearch: boolean = false) => {

    if (!this.dirty.includes(col.name) && !event?.target?.value && event?.target?.validationMessage) {
      this.dirty.push(col.name);
      return;
    }
    else if ((event?.target?.value || !event?.target?.validationMessage) && this.dirty.includes(col.name)) {
      this.dirty = this.dirty.filter((ele: string) => ele != col.name)
    }
    // if (event.target.value.length < 3) return;
    const columnIndex = this.payloadColumns.findIndex(
      (col) => col.data === column.prop
    );
    let payloadColumns = [];
    if (this.searchPayloadColumns.length > 0) {
      payloadColumns = JSON.parse(JSON.stringify(this.searchPayloadColumns));
    } else {
      payloadColumns = JSON.parse(JSON.stringify(this.payloadColumns));
    }
    payloadColumns[columnIndex].search.value = isFromSelectSearch ? event.value : event?.target?.value || '';
    if (col.fieldType === 'VARCHAR(15)') {
      payloadColumns[columnIndex].search.value = this.maskservice.applyMask(event?.target?.value, '0000000000');
    }
    this.searchPayloadColumns = payloadColumns;
    if ((isFromSelectSearch || event?.key === "Enter") && (event?.target?.value || !event?.target?.validationMessage)) {
      this.currentPageNumber = 0;
      this.lastPageOffsetForFocus = 0;
      this.emitPayload();
    }
  };

  selectSelected = (event: any, column: DatatableColumn, col: DatatableColumn) => {
    // if (event.target.value.length < 3) return;
    const columnIndex = this.payloadColumns.findIndex(
      (col) => col.data === column.prop
    );
    let payloadColumns = [];
    if (this.searchPayloadColumns.length > 0) {
      payloadColumns = JSON.parse(JSON.stringify(this.searchPayloadColumns));
    } else {
      payloadColumns = JSON.parse(JSON.stringify(this.payloadColumns));
    }
    payloadColumns[columnIndex].search.value = col.fieldType === 'ENUM2' ? event.map((option: any) => option.value).join(';') : event?.value || '';
    this.searchPayloadColumns = payloadColumns;
    this.currentPageNumber = 0;
    this.lastPageOffsetForFocus = 0;
    this.emitPayload();
  };

  selectFilterSelected = (col: DatatableColumn) => {
    if ((Array.isArray(col.filterValue) && col.filterValue.length) || (!Array.isArray(col.filterValue) && col.filterValue) || (!Array.isArray(col.filterValue2) && col.filterValue2) || col.filterCondition === 'empty') {
      this.filteringColumns = this.filteringColumns.map((column: DatatableColumn) => {
        if (column[this.isColumnFilterAllowed ? 'fieldName' : 'fieldKey'] === col[this.isColumnFilterAllowed ? 'fieldName' : 'fieldKey']) {
          col.isFilterApplied = true;
          return JSON.parse(JSON.stringify(col));
        }
        return column;
      })
    } else {
      this.filteringColumns = this.filteringColumns.map((column: DatatableColumn) => {
        if (column[this.isColumnFilterAllowed ? 'fieldName' : 'fieldKey'] === col[this.isColumnFilterAllowed ? 'fieldName' : 'fieldKey']) {
          col.isFilterApplied = false;
          return JSON.parse(JSON.stringify(col));
        }
        return column;
      })
    }

    if (!Array.isArray(this.conditionObject)) {
      this.conditionObject = [];
    } else if (Array.isArray(this.conditionObject) && this.conditionObject.length) {
      this.conditionObject = JSON.parse(JSON.stringify(this.conditionObject))
      let conIndex = -1;
      this.conditionObject.forEach((condition: any, index: any) => {
        if (!this.isColumnFilterAllowed && condition.Conditions.find((con: any) => con.ConditionField.Key === col.fieldKey)) {
          conIndex = index;
        } else if (this.isColumnFilterAllowed && condition.Conditions.find((con: any) => con.ConditionFieldName === col.fieldName)) {
          conIndex = index;
        }
      })
      if (conIndex > -1) {
        this.conditionObject.splice(conIndex, 1);
      }
    }
    let conditions = [];
    if (col.fieldType === 'USER' || col.fieldType === 'ENUM' || col.fieldType === 'ENUM2' || col.isRelation) {
      if (this.bulkUpdateTableName !== 'Fields') {
        col.filterValue = col.filterValue.filter((option: any) => !!option);
      }
      conditions.push(...col.filterValue.map((option: any) => ({
        [this.isColumnFilterAllowed ? 'ConditionFieldName' : 'ConditionField']: this.isColumnFilterAllowed ? col.fieldName : { Key: col.fieldKey },
        OperationType: col.fieldType === 'ENUM2' ? 'contain' : 'is equal',
        Value: option
      })))
    } else if (col.fieldType === 'DATE' || col.fieldType === 'DATETIME' || datecreatedRegex.test(col.fieldName) || datemodifiedRegex.test(col.fieldName)) {
      let value;
      let date;
      let date2;
      let condition;
      if (col.filterCondition === 'today') {
        const formattedDate = moment(new Date(), "YYYY-MM-DD").format("YYYY-MM-DD");
        const startOfDay = `${formattedDate} 00:00:00`;
        const endOfDay = `${formattedDate} 23:59:59`;
        value = `${startOfDay},${endOfDay}`;
        condition = 'between_date'
      } else if (col.filterCondition === 'month') {
        const [year, month] = col.filterValue.split('-').map(Number);
        const firstDayOfNextMonth = new Date(year, month, 1);
        const lastDateOfMonth = new Date(firstDayOfNextMonth);
        lastDateOfMonth.setDate(lastDateOfMonth.getDate() - 1);
        value = col.filterValue + '-01,' + col.filterValue + `-${lastDateOfMonth.getDate()}`
        condition = 'between_date'
      } else if (col.filterCondition === 'year') {
        value = `${col.filterValue}-01-01,${col.filterValue}-12-31`;
        date = value.split(',')[0];
        date2 = value.split(',')[1];
        condition = 'between_date'
      } else {
        if (col.filterCondition === 'between_date') {
          if (col.filterValue2 && !col.filterValue) {
            condition = 'before_date';
          } else if (!col.filterValue2 && col.filterValue) {
            condition = 'after_date';
          } else {
            condition = col.filterCondition;
          }
        } else {
          condition = col.filterCondition;
        }
        value = col.filterValue && col.filterValue2 ? [col.filterValue, col.filterValue2].join(',') : col.filterValue ? col.filterValue : col.filterValue2 ? col.filterValue2 : ''
        date = col.filterValue;
        date2 = col.filterValue2;
      }
      conditions.push({
        [this.isColumnFilterAllowed ? 'ConditionFieldName' : 'ConditionField']: this.isColumnFilterAllowed ? col.fieldName : { Key: col.fieldKey },
        OperationType: condition,
        Value: value,
        Date: date,
        Date2: date2
      })
    } else if (!this.isStringField(col)) {
      conditions.push({
        [this.isColumnFilterAllowed ? 'ConditionFieldName' : 'ConditionField']: this.isColumnFilterAllowed ? col.fieldName : { Key: col.fieldKey },
        OperationType: col.filterCondition,
        Value: col.filterValue
      })
    } else if (col.fieldType === 'TINYINT(1)') {
      if (!(col.filterValue && col.filterValue2 || (col.filterValue == false && col.filterValue2 == false))) {
        conditions.push({
          [this.isColumnFilterAllowed ? 'ConditionFieldName' : 'ConditionField']: this.isColumnFilterAllowed ? col.fieldName : { Key: col.fieldKey },
          OperationType: 'is equal',
          Value: col.filterValue ? 1 : 0
        })
      }
    } else if ((col.fieldType === 'INT' && !col.isRelation && !datecreatedRegex.test(col.fieldName) && !datemodifiedRegex.test(col.fieldName)) || col.fieldType === 'DOUBLE' || col.fieldType === 'DECIMAL(20,2)') {
      conditions.push({
        [this.isColumnFilterAllowed ? 'ConditionFieldName' : 'ConditionField']: this.isColumnFilterAllowed ? col.fieldName : { Key: col.fieldKey },
        OperationType: 'greater than or equal to',
        Value: col.filterValue ? col.filterValue : col.minValue
      })
      conditions.push({
        [this.isColumnFilterAllowed ? 'ConditionFieldName' : 'ConditionField']: this.isColumnFilterAllowed ? col.fieldName : { Key: col.fieldKey },
        OperationType: 'less than or equal to',
        Value: col.filterValue2 ? col.filterValue2 : col.maxValue
      })
    }
    if (conditions.length) {
      this.conditionObject.push({
        Type: (col.fieldType === 'INT' && !col.isRelation && !datecreatedRegex.test(col.fieldName) && !datemodifiedRegex.test(col.fieldName)) || col.fieldType === 'DOUBLE' || col.fieldType === 'DECIMAL(20,2)' ? 'all' : 'any',
        Conditions: conditions
      })
      this.conditionReportKey = this.reportId || this.dataTableReportId;
    }
    // const openedFilterBox:any = document.getElementById(`filterBox`);
    if (this.openedToolTip) {
      this.setColValues();
      this.openedToolTip.close();
    }
    if (this.reportId || this.dataTableReportId) {
      const filterObject = {
        filteringColumns: this.filteringColumns.map(column => ({
          [this.isColumnFilterAllowed ? 'fieldName' : 'fieldKey']: column[this.isColumnFilterAllowed ? 'fieldName' : 'fieldKey'],
          filterCondition: column.filterCondition,
          filterValue: column.filterValue,
          filterValue2: column.filterValue2,
          isFilterApplied: column.isFilterApplied,
        })),
        conditionObject: this.conditionObject
      }
      this.cookieService.put(`datatable_${this.fromDashboard}_${this.isEmbeddedReport}_${this.reportId || this.dataTableReportId}`, JSON.stringify(filterObject));
    }
    this.currentPageNumber = 0;
    this.lastPageOffsetForFocus = 0;
    this.emitPayload();
    this.filterCol = null;
  };

  isStringField(col: any) {
    const stringFieldTypes = ['INT', 'DOUBLE', 'DECIMAL(20,2)', 'TINYINT(1)', 'DATE', 'DATETIME', 'USER', 'ENUM', 'ENUM2'];
    return stringFieldTypes.includes(col?.fieldType) || col?.isRelation;
  }

  clearFilterForCol(filterCol: any): void {
    this.resetFilterColumn(filterCol);
    this.updateConditionObject(filterCol);
    this.resetFilterState();
    this.updateCookies();
    this.emitPayload();
  }

  private resetFilterColumn(filterCol: any): void {
    this.filteringColumns = this.filteringColumns.map((column: DatatableColumn) => {
      if (column[this.isColumnFilterAllowed ? 'fieldName' : 'fieldKey'] === filterCol[this.isColumnFilterAllowed ? 'fieldName' : 'fieldKey']) {
        column.isFilterApplied = false;
        // Reset the values in the actual filteringColumns array
        column.filterValue = this.isMultiValueFilter(column) ? [] : '';
        // Bifurcate: FLAG OFF uses '', FLAG ON uses null
        column.filterCondition = this.enableDataTableAccessibilityValue ? null : '';
        column.filterValue2 = '';
        if (this.isNumericFilter(column)) {
          column.minValue = +column.minValue;
          column.maxValue = +column.maxValue;
          column.filterValue = column.minValue;
          column.filterValue2 = column.maxValue;
        }
      }
      return column;
    });

    // Also reset the copy for immediate feedback
    filterCol.filterValue = this.isMultiValueFilter(filterCol) ? [] : '';
    // Bifurcate: FLAG OFF uses '', FLAG ON uses null
    filterCol.filterCondition = this.enableDataTableAccessibilityValue ? null : '';
    filterCol.filterValue2 = '';
    if (this.isNumericFilter(filterCol)) {
      filterCol.minValue = +filterCol.minValue;
      filterCol.maxValue = +filterCol.maxValue;
      filterCol.filterValue = filterCol.minValue;
      filterCol.filterValue2 = filterCol.maxValue;
    }
  }

  private updateConditionObject(filterCol: any): void {
    if (Array.isArray(this.conditionObject) && this.conditionObject.length) {
      this.conditionObject = JSON.parse(JSON.stringify(this.conditionObject));
      const conditionIndex = this.findConditionIndex(filterCol);
      if (conditionIndex > -1) {
        this.conditionObject.splice(conditionIndex, 1);
      }
    }
  }

  private resetFilterState(): void {
    this.currentPageNumber = 0;
    this.lastPageOffsetForFocus = 0;
    this.filterCol = null;
    if (this.openedToolTip) {
      this.setColValues();
      this.openedToolTip.close();
    }
  }

  private updateCookies(): void {
    if (this.reportId || this.dataTableReportId) {
      const cookieKey = `datatable_${this.fromDashboard}_${this.isEmbeddedReport}_${this.reportId || this.dataTableReportId}`;
      this.cookieService.remove(cookieKey);

      const filterObject = {
        filteringColumns: this.filteringColumns.map((column) => ({
          [this.isColumnFilterAllowed ? 'fieldName' : 'fieldKey']: column[this.isColumnFilterAllowed ? 'fieldName' : 'fieldKey'],
          filterCondition: column.filterCondition,
          filterValue: column.filterValue,
          filterValue2: column.filterValue2,
          isFilterApplied: column.isFilterApplied,
        })),
        conditionObject: this.conditionObject,
      };

      this.cookieService.put(cookieKey, JSON.stringify(filterObject));
    }
  }

  private isMultiValueFilter(filterCol: any): boolean {
    return ['USER', 'ENUM', 'ENUM2'].includes(filterCol.fieldType) || filterCol.isRelation;
  }

  private isNumericFilter(filterCol: any): boolean {
    return ['INT', 'DOUBLE', 'DECIMAL(20,2)'].includes(filterCol.fieldType) && !filterCol.isRelation;
  }

  private findConditionIndex(filterCol: any): number {
    return this.conditionObject.findIndex((condition: any) =>
      !this.isColumnFilterAllowed
        ? condition.Conditions.some((con: any) => con.ConditionField.Key === filterCol.fieldKey)
        : condition.Conditions.some((con: any) => con.ConditionFieldName === filterCol.fieldName)
    );
  }

  /**
   * toggle expand a group
   */
  toggleExpandGroup = (group: any) => {
    this.table.groupHeader.toggleExpandGroup(group);
  };

  /**
   * Select groupby name for grouping
   * @returns name of property to group with
   */
  get groupByName() {
    const tableId =
      this.columns &&
        this.columns.length > 0 &&
        this.columns[0].ApplicationTableId
        ? this.columns[0].ApplicationTableId
        : 0;
    if (this.isGrouping && this.payloadColumns[this.payloadSortBy[0].column])
      return this.payloadColumns[this.payloadSortBy[0].column].data;
    if ((this.summary?.GroupBy || this.embeddedGroupColumn) && tableId != 0) {
      let groupBy = this.summary?.GroupBy || this.embeddedGroupColumn;
      let regex = new RegExp('^tbl.*');
      if (!regex.test(groupBy)) groupBy = `tbl${tableId}_${groupBy}`;
      return groupBy;
    }
    return '';
  }

  /**
   * Re render the table (fixes grouping)
   */
  reRenderTable = () => {
    this.renderTable = false;
    this.cdr.detectChanges();
    setTimeout(() => {
      this.renderTable = true;
      this.cdr.detectChanges();
      // Re-setup header scroll sync after table is re-rendered
      setTimeout(() => {
        this.setupHeaderScrollSync();
      }, 50);
    }, 0);
  };

  /**
   * Handle the table layout on window - Resize
   */
  @HostListener('window:resize', ['$event'])
  onTableResize(event: any): void {
    this.resizeSubject.next();
    this.cdr.detectChanges();
  }

  /**
   * Re render the Column according to screen Resize
   */
  reRenderColumn(): void {
    this.renderColumn = false;
    this.cdr.detectChanges();
    this.renderColumn = true;
  }

  /**
   * Convert field name to title name
   * @param fieldName
   */
  fieldToTitle = (fieldName: string) => {
    if (fieldName.includes('_') && fieldName.includes('tbl')) {
      let name = fieldName.split('_').slice(1).join(' ');
      return name;
    } else if (fieldName.includes('_')) {
      let name = fieldName.split('_').join(' ');
      return name;
    }
    return fieldName;
  };

  ngOnDestroy() {
    // Clean up header scroll sync
    this.removeHeaderScrollSync();

    this.subsink.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();

    // Clear large object references
    this.rows = [];
    this.columns = [];
    this.filteringColumns = [];
    this.selectedItems = [];
    this.payloadColumns = [];
    this.searchPayloadColumns = [];
    this.conditionObject = null;
    this.getValueCache.clear();
    this.valueAlignCache.clear();

    // Clear localStorage if necessary
    if (this.isColumnFilter && (this.route.currentRoute().includes(this.reportId || this.dataTableReportId) && (this.route.currentRoute().includes('details') || (this.route.currentRoute().includes('form') && (this.route.currentRoute().includes('edit') || this.route.currentRoute().includes('add'))))) || this.preventFilter) {
      localStorage.setItem('isTableHasFilter', 'true');
    }
  }

  /**
   * @returns true if hyperlink
   */
  isFile = (column: any) => {
    const col = this.columns?.find((col) => col.FieldName === column.prop);
    return col?.FieldType === 'FILE';
  };

  /**
   * @returns true if hyperlink
   */
  isHyperlink = (column: any) => {
    const col = this.columns?.find((col) => col.FieldName === column.prop);
    return ((col?.LookupTargetFieldKey || col?.IsSummaryField) && (col?.ReportColumnSettings?.IncludeLink || col?.FieldType === 'FILE')) || col?.IsUrl;
  };

  isUrl = (column: any) => {
    const col = this.columns?.find((col) => col.FieldName === column.prop);
    return col?.IsUrl;
  };

  /**
   * Open file in new tab
   * @param row
   * @param column
   */
  fileLink = (row: any, column: any, columnData: DatatableColumn, isDownload: boolean = false) => {
    const col = this.columns?.find((col) => col.FieldName === column.prop);
    if (col?.FieldType === 'FILE' && columnData.isHyperLink && isDownload) {
      window.open(row[column.prop + '_url'] + '/true', '_blank');
    } else if (col?.FieldType === 'FILE' && (!columnData.isHyperLink || !isDownload)) {
      window.open(row[column.prop + '_url'], '_blank');
    }
  };

  linkText = (row: any, column: any) => {
    const col = this.columns?.find((col) => col.FieldName === column.prop);
    const val = row[column.prop];
    if (col?.IsUrl && val) {
      if (col.LinkText != '' && col.LinkText != null) return col.LinkText;
      else return val;
    }
  };

  /**
   * Generate route link for hyper link
   * @param value
   */
  routeLink = (row: any, column: any) => {
    const col = this.columns?.find((col) => col.FieldName === column.prop);
    const val = row[column.prop];
    if (col?.IsUrl && val) {
      var keywords = [
        'apps',
        'tables',
        'reports',
        'datatable',
        'view',
        'details',
      ];
      if (col.IsExternalUrl) {
        return val;
      } else {
        if (val.includes(keywords)) {
          const [appId, tableId, id, reportId] = val.split('/').slice(3);
          return [
            '/apps',
            appId,
            'tables',
            tableId,
            'reports',
            'datatable',
            'view',
            reportId,
            'details',
            id,
          ];
        } else {
          return val;
        }
      }
    }
    if (col?.IsSummaryField) {
      return [
        '/apps',
        this.route.getApplicationKey(),
        'tables',
        col.ApplicationTableKey,
        'reports',
        'datatable',
        'view',
        this.route.paramMap.reportId == undefined ||
          this.route.paramMap.reportId == null ||
          this.route.paramMap.reportId == ''
          ? 0
          : this.route.paramMap.reportId,
        'details',
        row['id'],
        'summaryFieldId',
        col.Key,
      ];
    }

    //Fall through for lookup fields and relation fields
    const colName = column.prop;
    const colParentName = `${colName}_parent_id`;
    const colParentTableKey = `${colName}_parent_table_key`;
    return [
      '/apps',
      this.route.getApplicationKey(),
      'tables',
      row[colParentTableKey],
      'reports',
      'details',
      'view',
      row[colParentName],
      0,
      col?.ReportColumnSettings?.ParentFormKey ?? 0
    ];

  };

  isCheckboxChecked(value: string | boolean) {
    return (value === 'True' || ((typeof value === 'boolean') && value) || value === 'true')
  }

  private getValueCache = new Map<string, any>();
  /**
   * Value to render
   * @param column
   * @param value
   * @param row
   */
  getValue(column: DatatableColumn, value: any, row: any = {}): any {
    const cacheKey = `${column.prop}-${value}-${row.id}`;
    if (this.getValueCache.has(cacheKey)) {
      return this.getValueCache.get(cacheKey);
    }

    const col = this.columns?.find((col) => col.FieldName === column.prop);
    if (col?.IsRelation) {
      value = row[col.FieldName + '_proxy'] || value;
    }

    let result = value || '-';
    if (col?.FieldType === 'DECIMAL(20,2)' || col?.ProxyFieldType === 'DECIMAL(20,2)') {
      if (!value) return '';
      const val = value.split('.');
      const isNegative = val[0]?.startsWith('-');
      const absoluteValue = isNegative ? val[0].slice(1) : val[0];
      const formattedValue = `$${this.separator(absoluteValue)}${val[1] ? '.' + val[1] : ''}`;
      result = isNegative ? `-${formattedValue}` : formattedValue;
    } else if (col?.FieldType === 'VARCHAR(15)' || col?.ProxyFieldType === 'VARCHAR(15)') {
      result = value?.toString().replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3') || '-';
    }

    this.getValueCache.set(cacheKey, result);
    return result;
  }

  getEncryptedValue(row: any, col: DatatableColumn) {
    return row[col.fieldName + '_decrypted'] || row[col.fieldName + '_proxy_decrypted'] || '-';
  }

  decryptEncryptField(event: any, col: DatatableColumn, row: any, value: any) {
    event.stopPropagation();
    const encryptedCol = document.getElementById(`encrypted-${col.fieldKey}-${row.id}`);
    if (encryptedCol) {
      if (encryptedCol.innerText === this.getValue(col, value, row)) {
        encryptedCol.innerText = this.getEncryptedValue(row, col);
      } else {
        encryptedCol.innerText = this.getValue(col, value, row);
      }
    }
  }

  separator = (str: string): any => {
    if (str.length <= 3) return str;
    return (
      this.separator(str.substring(0, str.length - 3)) +
      ',' +
      str.slice(str.length - 3)
    );
  };

  private valueAlignCache = new Map<string, string>();

  /**
   * Alignment of value
   */
  valueAlign(column: DatatableColumn) {
    const cacheKey = column.prop;
    if (this.valueAlignCache.has(cacheKey) && this.valueAlignCache.get(cacheKey)) {
      return this.valueAlignCache.get(cacheKey);
    }

    const col = this.columns?.find((col) => col.FieldName === column.prop);

    const isNumericCol =
      (((col?.FieldType === 'DECIMAL(20,2)' || col?.FieldType === 'INT' || col?.FieldType === 'DOUBLE') &&
        !col.IsRelation) ||
        ((col?.ProxyFieldType === 'INT' || col?.ProxyFieldType === 'DECIMAL(20,2)' || col?.ProxyFieldType === 'DOUBLE') &&
          col.IsRelation)) &&
        !(datecreatedRegex.test(col.FieldName) || datemodifiedRegex.test(col.FieldName) || col.Name === 'Record Id');

    const isDateCol = (col?.FieldType === 'DATE' || col?.FieldType === 'DATETIME') && !col.IsRelation;

    const align = (isNumericCol || isDateCol) ? 'end' : 'start';

    this.valueAlignCache.set(cacheKey, align);
    return align;
  }

  /**
   * Renter template according to value
   */
  selectTemplate = (value: string, column: DatatableColumn, col: DatatableColumn) => {
    if (
      (this.route.currentRoute().includes('reports') && col.fieldName === 'default') ||
      (this.route.currentRoute().includes('fields') && col.fieldName === "isnullable") ||
      (this.route.currentRoute().includes('settings/user-management/roles') && col.fieldName === "is_public?")
    ) {
      return this.checkbox;
    }
    if (
      this.externalPaging &&
      this.showEnteriesDropdown &&
      !this.isBulkUpdateEnable &&
      !this.isBulkDeleteEnable &&
      !this.externalSorting
    )
      return this.fieldsreports;
    if (col.fieldType === 'PII2' || col.proxyFieldType === 'PII2') {
      return this.encryptedField;
    }
    switch (value) {
      case 'True':
      case 'False':
        if (
          this.route.currentRoute().includes('settings/user-management') &&
          column.name == 'Active'
        )
          return this.activeUsers;
        if (
          this.route.currentRoute().includes('settings/user-management') &&
          column.name == 'Email Alerts'
        )
          return this.emailAlerts;
        if (col.fieldType === 'TINYINT(1)' || col.proxyFieldType === 'TINYINT(1)') return this.checkbox;
        return this.default;
      case 'Invited':
        if (
          this.route.currentRoute().includes('settings/user-management') &&
          column.name == 'Active'
        )
          return this.invitedUsers;
        return this.default;
      default:
        return this.default;
    }
  };

  changeSelectable = () => {
    this.isSelectable = !this.isSelectable;
    this.clearSelection();
  };

  singleSelectRow: any;

  /**
   * On row select by checkbox
   */
  onSelect = ({ selected }: any) => {
    this.selectedItems = [];
    if (selected && selected.length > 0) {
      selected.forEach((el: any) => {
        if (this.rows.includes(el)) {
          this.selectedItems.push(el);
        }
      });
      if (this.singleSelect) {
        this.singleSelectRow = selected[0].id;
        this.emitPayload();
      }
    }
    // this.selectedItems = selected || [];
  };

  deleteSelected = () => {
    let rids = new Array<number>();
    this.selectedItems.filter((item) => !(item.colSearch || item.colFilter)).forEach((item) => rids.push(item.id));
    var currentRoute = this.route.currentRoute();
    var tableId = this.route.getTableKey();
    if (isNaN(tableId)) tableId = this.tableId;
    if (
      (currentRoute.includes('reports/datatable/view') ||
        currentRoute.includes('reports/details/view')) &&
      (currentRoute.includes('details') ||
        currentRoute.includes('summaryFieldId'))
    ) {
      tableId = this.tableId;
    }

    let id = rids.join(', ');
    const payload: DependentRecords = {
      ApplicationKey: this.route.getApplicationKey(),
      ApplicationTableKey: tableId,
      IsBulkUpdateDelete: true,
      Where: {
        Rids: rids,
      },
    };
    if (this.datatableType == type.Datatable) {
      this.store.dispatch(
        new LoadDependentRecords(this.route.getTableKey(), id, payload)
      );

      let getDependentRecordsSubscriber = this.store
        .pipe(
          select(getDependentRecords),
          filter(
            (records) =>
              !!records[this.route.getTableKey()] &&
              !!records[this.route.getTableKey()][id]
          ),
          map((records) => records[this.route.getTableKey()][id]),
          take(1)
        )
        .subscribe((records: any) => {
          if (!Object.keys(records.data).length) {
            // delete record by id
            this.alert
              .fire(<SweetAlertOptions>alertPresets.delete)
              .then(async (res) => {
                if (res.isConfirmed) {
                  this.clearSelection();
                  try {
                    await this.action.dispatch(
                      OpTypes.DELETE,
                      new DeleteObject(payload),
                      getFormOperations,
                      {
                        ...alertDefaultExtra,
                        success: {
                          ...alertDefaultExtra.success,
                          show: true,
                          title: 'Success',
                          message: 'Records Deleted'
                        }
                      }
                    );
                    this.trigger.next();
                  } catch (err) { }
                }
              });
          } else {
            let route = `/apps/${this.route.getApplicationKey()}/settings/relation-visualize`;
            let tag = `<a href="${route}" target="_blank">view hierarchy</a>`;
            let message = '';
            var map = new Map(Object.entries(records.data));

            if (map.size != 0) {
              if (map.size > rids.length) {
                message +=
                  'This will also delete all records in child tables:<br>';
              } else {
                message +=
                  'This will also delete all records in child table:<br>';
              }
              map.forEach(function (value: any, key: any) {
                if (value > 1) {
                  message += key + ' - ' + value + ' records<br>';
                } else if (value == 1) {
                  message += key + ' - ' + value + ' record<br>';
                }
              });

              if (message) {
                message += '<br>' + tag;
              }
              if (message) {
                message += '<br><br>' + 'Once you delete the records, there is no going back.';
              }
            }
            this.alert
              .fire(<SweetAlertOptions>{
                ...alertPresets.deleteRecords,
                html: message,
              })
              .then(async (res) => {
                if (res.isConfirmed) {
                  if (res.value === 'delete') {
                    this.selectedItems = [];
                    try {
                      await this.action.dispatch(
                        OpTypes.DELETE,
                        new DeleteObject(payload),
                        getFormOperations,
                        {
                          ...alertDefaultExtra,
                          success: {
                            ...alertDefaultExtra.success,
                            show: true,
                            title: 'Success',
                            message: 'Records Deleted'
                          }
                        }
                      );
                      this.trigger.next();
                    } catch (err) { }
                  } else {
                    this.alert
                      .fire(<SweetAlertOptions>{
                        ...alertPresets.deniedDeleteApp,
                        title: 'Confirmation Denied!'
                      })
                  }
                }
              });
          }
        });
      this.subsink.add(getDependentRecordsSubscriber);
    }
  };

  bulkMatch = async () => {
    let rids = new Array<number>();
    this.selectedItems.filter((item) => !(item.colSearch || item.colFilter)).forEach((item) => rids.push(item.id));

    const payload: any = {
      ApplicationTableKey: this.tableId,
      ActionType: 'update',
      // Where: { Rid: rids.length === 1 ? rids[0] : rids },
    };

    await this.alert.fire(<SweetAlertOptions>{
      ...alertPresets.delete,
      title: 'Are you sure?',
      text: 'This action will attach all records with parent table',
      confirmButtonText: 'Yes, match records',
    })
      .then(async (res) => {
        if (res.isConfirmed) {

          const editRecordId = this.lookupFieldValue ?? this.route.paramMap.id;

          if (!editRecordId) {
            this.alert.toast(
              ToastTypes.ERROR,
              `Unable to match records.`,
              `Edit record id is missing.`
            );
            return;
          }

          const obj = {
            Name: this.referenceFieldName,
            Info: null,
            Value: editRecordId,
            Type: 'INT',
            Base64EncodedFile: '',
          }
          const matchPayload = rids.map(id => ({ ...payload, Where: { Rid: id }, FieldsList: [obj] }));
          // payload.FieldsList = [obj];
          try {
            await this.action.dispatch(
              OpTypes.UPDATE,
              new SyncData(this.tableId, matchPayload),
              getFormOperations
            );
            this.embeddedMatched.emit();
            this.clearSelection();
          } catch (err) { }
        }
      })
  }

  isHtmlAllowed = (column: DatatableColumn) => {
    if (column.prop === 'default') return true;
    const col = this.columns?.find((col) => col.FieldName === column.prop);
    return col?.IsFormula && col?.IsHtmlAllowed;
  };
  isFormulaField = (column: DatatableColumn) => {
    if (column.prop === 'default') return true;
    const col = this.columns?.find((col) => col.FieldName === column.prop);
    return col?.IsFormula
  };

  isEmailField = (column: DatatableColumn) => {
    if (column.prop === 'default') return true;
    const col = this.columns?.find((col) => col.FieldName === column.prop);
    return col?.FieldType === 'EMAIL' || col?.ProxyFieldType === 'EMAIL';
  };

  /**
   * Clear selected items
   */
  clearSelection = () => {
    this.selectedItems = [];
    if (this.table) {
      this.table.selected = [];
    }
  };

  getRowClass(row: any) {
    if (row?.isgrouped == 'true') return 'selectedRow';
    else return '';
  }

  getCellClass(row: any) {
    if (row?.row?.isgrouped == 'true') return 'removeCheckBox';
    else if (row?.column?.name === "Actions")
      return 'justify-content-center';
    else return '';
  }

  get footerText() {
    if (!this.dataset?.RecordsTotal) return '';
    const upperLimit = this.showEnteriesDropdown
      ? this.currentPageNumber * this.page.size + this.page.size
      : 500;
    return `Showing ${this.currentPageNumber * this.page.size + 1} to
    ${upperLimit > this.dataset?.RecordsTotal
        ? this.dataset?.RecordsTotal
        : upperLimit
      } of
    ${this.dataset?.RecordsTotal.toLocaleString('en-US')} entries`;
  }

  /**
   * This is a bug in ngx-datatable which makes table do client side sorting even if "externalSorting" is true
   * Reference: https://github.com/swimlane/ngx-datatable/issues/1470#issuecomment-419357785
   */
  noClientSort = () => 0;

  clearColumns = (rows: any) => {
    let tempRows = [];
    let standardCols: any = [];
    let fileFields: any = [];
    this.visibleColumns.forEach(column => {
      if (column.fieldType === 'PII2' || column.proxyFieldType === 'PII2') {
        standardCols.push(column.fieldName);
      }
      if (column.fieldType === 'FILE') {
        fileFields.push(column.fieldName)
      }
    });
    for (let row of rows) {
      if (row.colFilter || row.colSearch) {
        continue;
      }
      let tempObj: any = {};
      let decryptKey: any = [];
      for (let key in row) {
        let orgKey = key;
        if ((this.reportId || this.dataTableReportId) && !this.visibleColumns.find(column => column.fieldName === orgKey)) {
          continue;
        }
        if (key.includes('tbl')) {
          key = this.visibleColumns.find(column => column.fieldName === key)?.name || key
            .split('_')
            .filter(part => !part.includes('tbl'))
            .map((w) => w[0].toUpperCase() + w.substring(1).toLowerCase())
            .join(' ');
        }
        if (this.reportId || this.dataTableReportId) {
          if (standardCols.includes(orgKey) && Object.keys(row).includes(orgKey + '_decrypted')) {
            tempObj[key] = row[orgKey + '_decrypted'];
            decryptKey.push(orgKey + '_decrypted');
            continue;
          } else if (standardCols.includes(orgKey) && Object.keys(row).includes(orgKey + '_proxy_decrypted')) {
            tempObj[key] = row[orgKey + '_proxy_decrypted'];
            decryptKey.push(orgKey + '_proxy_decrypted');
            continue;
          } else if (decryptKey.includes(orgKey)) continue;
        }
        if (this.visibleColumns.find(column => column.fieldName === orgKey)?.isRelation && row[orgKey + '_proxy']) {
          tempObj[key] = row[orgKey + '_proxy'];
          continue
        }
        if (fileFields.includes(orgKey) && Object.keys(row).includes(orgKey + '_url')) {
          tempObj[key + ' URL'] = row[orgKey + '_url'];
        }
        tempObj[key] = row[orgKey];
      }

      tempRows.push(tempObj);
    }
    return tempRows;
  };

  getColumnClass(column: any, value: any, row: any) {
    let obj: any = {};
    obj['blue-check-icon'] = value === ' ' && column.name === 'Default';
    obj['yellow-key-icon'] = value === 'Record Id' && column.name === 'Name';
    obj['text-success'] =
      column.name === 'Status' && value === 'Succeed' ? true : false;
    obj['text-danger'] =
      column.name === 'Status' && value === 'Failed' ? true : false;
    obj['text-info'] =
      column.name === 'Status' && value === 'Executing' ? true : false;
    obj['blue-key-icon'] = row.isUnique && column.name === 'Name';
    return obj;
  }

  getColWidth(col: DatatableColumn) {
    if (col && col.width) return col.width;
    let width = col.name?.length * 13;
    if (width < 100) return 120;
    if (width === 100) return 140;
    else return width;
  }

  getColMinWidth(col: DatatableColumn) {
    if (this.isColumnSearch) {
      let width = col.name?.length * 13;
      if (width < 200) return 220;
      return width
    }
    else return 120
  }

  updateHeaderFormat() {
    Array.from(document.getElementsByTagName('datatable-header-cell')).forEach(
      (element: any) => {
        if (this.isLightHeader)
          element.classList.add('datatable-header-cell-light');
        else element.classList.add('datatable-header-cell-dark');
      }
    );
  }

  bulkUpdate = () => {
    return import('./bulk-update/bulk-update.component').then(
      ({ BulkUpdateComponent }) => {
        const modal = this.modal.open(BulkUpdateComponent, {
          windowClass: 'modal-custom',
          centered: true,
          backdrop: 'static',
          animation: true,
          keyboard: false,
          size: 'xl',
        });
        modal.componentInstance.selectedItems = this.selectedItems;
        modal.componentInstance.fields = this.columns;
        modal.componentInstance.reportKey = this.dataTableReportId || this.reportId;
        modal.componentInstance.relatedTableId =
          this.tableId != '0' ? this.tableId : this.route.getTableKey();
        modal.result
          .then((data) => {
            this.emitPayload();
            this.onRecordEdit.emit(true);
          })
          .catch((reason) => {
            if (reason && !isNaN(this.recordId)) {
              this.store.dispatch(
                new LoadDetails(
                  this.route.getTableKey(),
                  this.reportId,
                  this.recordId
                )
              );
            }
            this.clearSelection();
            this.emitPayload();
            this.onRecordEdit.emit(true);
          });
      });
  };


  bulkUpdateRecords = () => {
    import('./datatable-bulk-edit/datatable-bulk-edit.component').then(
      ({ DatatableBulkEditComponent }) => {
        const modal = this.modal.open(DatatableBulkEditComponent, {
          windowClass: 'modal-custom',
          centered: true,
          backdrop: 'static',
          animation: true,
          keyboard: false,
          size: 'xl',
        });
        modal.componentInstance.selectedItems = this.selectedItems;
        modal.componentInstance.columns = JSON.parse(JSON.stringify(this.columns));
        modal.componentInstance.tableId = this.tableId !== '0' ? this.tableId : this.route.getTableKey();
        modal.componentInstance.datatableTableName = this.bulkUpdateTableName;
        modal.result
          .then((data) => {
            this.emitPayload();
            this.tableUpdated.emit();
          })
          .catch((reason) => {
            this.clearSelection();
            this.emitPayload();
            this.tableUpdated.emit();
          });
      });
  };

  openActivateUserModal = (event: any, row: any) => {
    this.payloadActivateUser = {
      Id: row.id,
      ApplicationKey: this.route.getApplicationKey(),
      Active: event.target.checked == true ? 1 : 0,
      Email: row.email,
      fromAllApps: row.fromAllApps
    };
    this.buttonText = event.target.checked == true ? "Activate" : "Deactivate";
    const modelRef = this.modal.open(this.updateActiveTemplate, {
      windowClass: 'modal-custom',
      centered: true,
      backdrop: 'static',
      animation: true,
      keyboard: false,
      size: 'lg'
    });
    modelRef.result
      .then(() => {
        this.reRenderTable();
      })
      .catch(() => {
        this.reRenderTable();;
      });
  };

  updateActivateUser = async (fromAllApps: boolean) => {
    this.payloadActivateUser.fromAllApps = fromAllApps;
    try {
      await this.action.dispatch(
        OpTypes.UPDATE,
        new UpdateActive(this.payloadActivateUser),
        getUserOperations
      ).then(() => {
        this.modal.dismissAll();
        this.reRenderTable();
      })
    } catch (err) { }

  }

  OnEmailAlerts = async (event: any, row: any) => {
    let payload = {
      Id: row.id,
      EmailAlerts: event.target.checked == true ? 1 : 0,
    };
    try {
      await this.action.dispatch(
        OpTypes.UPDATE,
        new ToggleEmailAlerts(payload),
        getUserOperations
      ).then(() => {
        this.reRenderTable();
      })
    } catch (err) { }
  };

  groupingTitleContent(data: any, summary: any, group: any) {
    const titleText = (this.fieldToTitle(
      data ? data : summary?.GroupBy || this.embeddedGroupColumn
    ).includes('datecreated') ||
      this.fieldToTitle(
        data ? data : summary?.GroupBy || this.embeddedGroupColumn
      ).includes('datemodified')
      ? group.key
      : group.key ||
      this.fieldToTitle(
        data ? data : summary?.GroupBy || this.embeddedGroupColumn
      ));

    // Bifurcate formatting based on feature flag
    if (this.enableDataTableAccessibilityValue) {
      // FLAG ON: Simple text format for better accessibility
      return titleText + `   (${this.getTotalRecords(group.value)})`;
    } else {
      // FLAG OFF: Original HTML span format
      return titleText + '<span class="total-records">(' + this.getTotalRecords(group.value) + ')</span>';
    }
  }

  get isVirtualizationEnabled() {
    return (
      (this.reportId || this.fromMapInDashboard) &&
      !this.isEmbeddedReport &&
      !this.isColumnGroupingEnable &&
      !this.summary?.GroupBy
    );
  }

  inputSelector(col: DatatableColumn) {
    let type = col.fieldType;
    if (col.isRelation) type = 'Related'
    if (
      col.fieldName === 'createdby' ||
      col.fieldName === 'modifiedby' ||
      createdbyRegex.test(col.fieldName) ||
      modifiedbyRegex.test(col.fieldName)
    )
      type = 'VARCHAR(255)';

    switch (type) {
      case 'USER':
      case 'Related':
        return this.selectSearchScroll;
      case 'ENUM':
      case 'ENUM2':
        return this.enumInput;
      default: return this.searchInput;
    }
  }

  get enableFilterSearch() {
    return this.reportId || this.dataTableReportId || this.allowColumnFilter;
  }

  inputFieldType(col: DatatableColumn) {
    if ((col.fieldType === 'INT' && !datecreatedRegex.test(col.fieldName) && !datemodifiedRegex.test(col.fieldName)) || col.fieldType === 'DECIMAL(20,2)' || col.fieldType === 'DOUBLE') {
      return 'number';
    } else if (col.fieldType === 'DATE' || datecreatedRegex.test(col.fieldName) || datemodifiedRegex.test(col.fieldName)) {
      return 'date';
    } else if (col.fieldType === 'DATETIME') {
      return 'datetime-local';
    } else if (col.fieldType === 'EMAIL') {
      return 'email'
    }
    return 'text';
  }

  setValue(event: any) {
    this.filterCol.filterValue = event.target.value;
  }

  onDatatableScroll(event: any) {
    let dropdown = document.querySelector('.ng-dropdown-panel-items.scroll-host');
    if (dropdown) this.renderer.setStyle(dropdown, 'display', 'none');
    if (this.openedToolTip) {
      this.setColValues();
      this.openedToolTip.close();
    }
    if (this.editModalContext) {
      this.editModalContext.close();
      this.isEditModalActive = null;
      this.isMouseOnLink = null;
    }
  }

  openedToolTip!: any;

  onOverLayOpen(event: any, tooltipInstance: any, column: any, value: any, row: any, col: any) {
    this.setColValues();
    if (this.openedToolTip) {
      this.openedToolTip.close();
    }
    this.openedToolTip = tooltipInstance;

    if (this.filterCol) {
      this.filterCol = null;
    }
    this.filterColumn = column;
    this.filterRow = value;
    this.filterValue = row;
    this.filterCol = JSON.parse(JSON.stringify(this.filteringColumns.find((column: any) => column[this.isColumnFilterAllowed ? 'fieldName' : 'fieldKey'] === col[this.isColumnFilterAllowed ? 'fieldName' : 'fieldKey'])));
    if ((col.fieldType === 'INT' && !col.isRelation && !datecreatedRegex.test(col.fieldName) && !datemodifiedRegex.test(col.fieldName)) || col.fieldType === 'DOUBLE' || col.fieldType === 'DECIMAL(20,2)') {
      this.filterCol.minValue = +this.filterCol.minValue;
      this.filterCol.maxValue = +this.filterCol.maxValue;
      this.filterCol.filterValue = this.filterCol.filterValue || this.filterCol.minValue;
      this.filterCol.filterValue2 = this.filterCol.filterValue2 || this.filterCol.maxValue;
    }
    this.openedToolTip.open()
  }

  setColValues() {
    if (!this.filterCol) return;
    this.filteringColumns = this.filteringColumns.map(column => {
      if (column[this.isColumnFilterAllowed ? 'fieldName' : 'fieldKey'] === this.filterCol[this.isColumnFilterAllowed ? 'fieldName' : 'fieldKey']) {
        column.filterValue = this.filterCol.filterValue;
        column.filterValue2 = this.filterCol.filterValue2;
        column.filterCondition = this.filterCol.filterCondition;
      }
      return column;
    })
  }

  onFilterPopoverShown(filterButtonElement: HTMLElement): void {
    // Store the filter button reference for focus restoration
    this.currentFilterButton = filterButtonElement;

    setTimeout(() => {
      // Query for all focusable elements in the filter popup
      const container = document.querySelector('.custom-popup .filter-box') as HTMLElement;
      if (!container) return;

      this.popoverContainer = container;

      // Helper function to get current focusable elements (dynamic query)
      const getFocusableElements = (): HTMLElement[] => {
        return Array.from(
          container.querySelectorAll(
            'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), ng-select:not([disabled]), .ng-select:not(.ng-select-disabled)'
          )
        ) as HTMLElement[];
      };

      // Helper function to properly focus an element (handles ng-select specially)
      const focusElement = (element: HTMLElement) => {
        // Check if element is ng-select
        if (element.tagName.toLowerCase() === 'ng-select' || element.classList.contains('ng-select')) {
          // Find the input inside ng-select
          const input = element.querySelector('input') as HTMLElement;
          if (input) {
            input.focus();
            return;
          }
        }
        // Default focus for other elements
        element.focus();
      };

      // Helper function to handle Tab forward navigation
      const handleTabForward = (
        focusableElements: HTMLElement[],
        isOnFilterButton: boolean,
        activeElement: HTMLElement,
        e: KeyboardEvent
      ) => {
        if (isOnFilterButton) {
          e.preventDefault();
          focusElement(focusableElements[0]); // From filter button to first element
        } else {
          const focusedIndex = focusableElements.findIndex(el =>
            el === activeElement || el.contains(activeElement)
          );
          if (focusedIndex === focusableElements.length - 1) {
            e.preventDefault();
            filterButtonElement.focus(); // From last element back to filter button
          }
        }
      };

      // Helper function to handle Shift+Tab backward navigation
      const handleTabBackward = (
        focusableElements: HTMLElement[],
        isOnFilterButton: boolean,
        activeElement: HTMLElement,
        e: KeyboardEvent
      ) => {
        if (isOnFilterButton) {
          e.preventDefault();
          focusElement(focusableElements[focusableElements.length - 1]); // From filter button to last element
        } else {
          const focusedIndex = focusableElements.findIndex(el =>
            el === activeElement || el.contains(activeElement)
          );
          if (focusedIndex === 0) {
            e.preventDefault();
            filterButtonElement.focus(); // From first element back to filter button
          }
        }
      };

      // Create focus trap that includes the filter button
      this.trapFocusListener = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;

        // Query focusable elements dynamically to account for enabled/disabled state changes
        const focusableElements = getFocusableElements();
        if (focusableElements.length === 0) return;

        const activeElement = document.activeElement as HTMLElement;
        const isOnFilterButton = activeElement === filterButtonElement;

        if (!e.shiftKey) {
          handleTabForward(focusableElements, isOnFilterButton, activeElement, e);
        } else {
          handleTabBackward(focusableElements, isOnFilterButton, activeElement, e);
        }
      };

      // Add listener to both filter button and container
      filterButtonElement.addEventListener('keydown', this.trapFocusListener);
      container.addEventListener('keydown', this.trapFocusListener);
    }, 100); // Delay to ensure popup is fully rendered
  }

  onFilterPopoverHidden(filterButtonElement: HTMLElement): void {
    // Remove event listeners when popover closes
    if (this.trapFocusListener) {
      filterButtonElement.removeEventListener('keydown', this.trapFocusListener);
      if (this.popoverContainer) {
        this.popoverContainer.removeEventListener('keydown', this.trapFocusListener);
      }
      this.trapFocusListener = null;
      this.popoverContainer = null;
    }

    // Restore focus to the filter button that opened the popup
    if (this.currentFilterButton) {
      setTimeout(() => {
        this.currentFilterButton?.focus();
        this.currentFilterButton = null;
      }, 100); // Small delay to ensure popup is fully closed
    }
  }

  get isInDetailsComponent() {
    return this.route.currentRoute().includes('/details')
  }

  isCheckboxEnabled = (row: any) => {
    let isEnabled = (this.isBulkDeleteEnable || this.isBulkUpdateEnable) && !this.isGrouping && !row.colFilter && !row.colSearch;
    if (isEnabled && this.actionColumnWidth <= 70) {
      this.actionColumnWidth += 45;
    }

    return isEnabled
  }

  isFilterSelected(col: DatatableColumn) {
    return this.filteringColumns.find(column => column[this.isColumnFilterAllowed ? 'fieldName' : 'fieldKey'] === col[this.isColumnFilterAllowed ? 'fieldName' : 'fieldKey'])?.isFilterApplied;
  }

  isFilterApplyDisabled(col: DatatableColumn): boolean {
    if (!this.isStringField(col)) {
      if (col?.filterCondition === 'empty') return false;
      if (!col?.filterCondition || !col?.filterValue) return true;
    }
    if (col?.fieldType === 'DATE' || col?.fieldType == 'DATETIME' || datecreatedRegex.test(col.fieldName) || datemodifiedRegex.test(col.fieldName)) {
      if (col?.filterCondition === 'today') return false;
      if (!col?.filterCondition || !col?.filterValue) return true;
    }
    if (col?.fieldType === 'ENUM' || col?.fieldType == 'ENUM2' || col?.fieldType === 'USER' || col?.isRelation) {
      if (!col.filterValue.length || (col?.filterValue?.length == 1 && col?.filterValue?.[0] == '' && this.bulkUpdateTableName !== 'Fields')) return true;
    }
    return false;
  }

  getFilterSliderOptions(col: DatatableColumn) {
    return {
      floor: col.minValue,
      ceil: col.maxValue,
      step: col.maxValue - col.minValue > 1000 ? 10 : col.maxValue - col.minValue < 1 ? 0.1 : 1,
      showTicksValues: false,
      getLegend: (value: number): string => {
        return `${value}`
      }
    }
  }

  isEditAllowed(col: DatatableColumn) {
    if (this.allowInlineEdit) {
      const colName = col.prop.startsWith('tbl') ? col.prop?.split('_')?.slice(1)?.join('_') : col.prop;
      if (this.isFormulaField(col) ||
        (colName === 'id') ||
        datecreatedRegex.test(col.prop) ||
        datemodifiedRegex.test(col.prop) ||
        createdbyRegex.test(col.prop) ||
        modifiedbyRegex.test(col.prop) ||
        (col.fieldType === 'FILE') ||
        (col.isLookup && !col.isRelation) || col.IsSummaryField) {
        return false
      }
      return true
    } else {
      return false;
    }
  }

  onMouseEnter(col: DatatableColumn, row: any) {
    if (this.isEditModalActive) return;
    this.isMouseOnLink = col.fieldKey + row.id;
  }

  onMouseLeave() {
    if (this.isEditModalActive) return;
    this.isMouseOnLink = null
  }

  openEditModal(template: any, col: DatatableColumn, row: any, value: any) {
    if (this.isEditModalActive === (col.fieldKey + row.id)) {
      this.editModalContext.close();
      this.isEditModalActive = null;
      this.isMouseOnLink = null;
      return;
    }
    if (this.editModalContext) {
      this.editModalContext.close();
      this.isEditModalActive = null;
    }
    let val = this.getValue(col, value, row);
    if ((col.fieldType === "DECIMAL(20,2)" || col.proxyFieldType === "DECIMAL(20,2)") && val.includes("$")) {
      val = val.replace(/[^0-9.-]/g, "");
    }


    this.isEditModalActive = col.fieldKey + row.id;
    this.editModalContext = template;
    this.editColumn = col;
    this.editRow = row;
    this.editValue = val;
    this.editModalContext.open();
  }

  onEditCancel() {
    this.editModalContext.close();
    this.isEditModalActive = null;
    this.isMouseOnLink = null;
  }

  onEditSubmit(value: any) {
    this.rows = JSON.parse(JSON.stringify(this.rows)).map((previousRow: any) => {
      if (previousRow.id === this.editRow.id) {
        previousRow[this.editColumn.prop] = value
      }
      return previousRow;
    });
    this.onEditCancel();
    this.emitPayload();
    this.onRecordEdit.emit(true);
  }

  handleMailClick(email: string): void {
    if (EMAIL_PATTERN.test(email)) {
      window.location.href = `mailto:${email}`;
    }
  }

  private setupHeaderScrollSync(): void {
    if (this.headerScrollListener) {
      this.removeHeaderScrollSync();
    }

    const datatableElement = this.table?.element;
    if (!datatableElement) return;

    const header = datatableElement.querySelector('.datatable-header') as HTMLElement;
    const body = datatableElement.querySelector('.datatable-body') as HTMLElement;

    if (!header || !body) return;

    let scrollHandledByEvent = false;

    // Intercept browser-initiated header scroll: reset header and redirect delta
    // to the body. This handles forward Tab where the browser auto-scrolls the
    // header to reveal the focused element.
    this.headerScrollListener = () => {
      if (header.scrollLeft !== 0) {
        const scrollDelta = header.scrollLeft;
        header.scrollLeft = 0;
        body.scrollLeft += scrollDelta;
        scrollHandledByEvent = true;
      }
    };

    header.addEventListener('scroll', this.headerScrollListener);

    // Fallback: scroll body to reveal focused header cells when no header scroll
    // fires. This handles Shift+Tab where the browser doesn't auto-scroll because
    // the target element is within the header's native viewport (scrollLeft=0) but
    // visually off-screen due to CSS transforms. Uses setTimeout(0) to run after
    // the browser's focus-scroll cycle so the scroll listener gets first shot.
    const SCROLL_PADDING = 4;

    this.headerFocusListener = (event: FocusEvent) => {
      const target = event.target as HTMLElement;
      if (!target) return;

      const headerCell = target.closest('datatable-header-cell') as HTMLElement;
      if (!headerCell) return;

      scrollHandledByEvent = false;

      setTimeout(() => {
        if (scrollHandledByEvent) return;

        const headerRect = header.getBoundingClientRect();
        const cellRect = headerCell.getBoundingClientRect();

        // Account for pinned left columns (e.g., actions) that overlay the left edge
        const pinnedLeft = header.querySelector('.datatable-row-left') as HTMLElement;
        const leftBound = pinnedLeft ? pinnedLeft.getBoundingClientRect().right : headerRect.left;

        if (cellRect.left < leftBound) {
          body.scrollLeft += cellRect.left - leftBound - SCROLL_PADDING;
        } else if (cellRect.right > headerRect.right) {
          body.scrollLeft += cellRect.right - headerRect.right + SCROLL_PADDING;
        }
      }, 0);
    };

    header.addEventListener('focusin', this.headerFocusListener);
  }

  private removeHeaderScrollSync(): void {
    const datatableElement = this.table?.element;
    if (datatableElement) {
      const header = datatableElement.querySelector('.datatable-header') as HTMLElement;

      if (header) {
        if (this.headerScrollListener) {
          header.removeEventListener('scroll', this.headerScrollListener);
        }
        if (this.headerFocusListener) {
          header.removeEventListener('focusin', this.headerFocusListener);
        }
      }
    }

    this.headerScrollListener = null;
    this.headerFocusListener = null;
  }

  get isColumnFilterAllowed() {
    return this.allowColumnFilter && !(this.auditLogId || this.notificationLogId)
  }
}
